import { AxiosResponse, AxiosInstance, CancelToken } from 'axios';

import { APISet, APIFunctionType } from '../packages/dao-generator';

/* ********************** */
/* ***** 基础类型定义 ***** */
/* ********************** */
export type UserId = number;
export type OrganizationId = number;
export type LibraryId = string;
export type SpaceId = string;
export type AccessToken = string;
export type ExpiresIn = number;
export type UserToken = string;
export type HTTP = AxiosInstance;
export type Partial<T> = {
  [key in keyof T]?: T[key];
};
export type EmptyData = string;
export type TeamId = number;

export interface AsyncTaskResult {
  status: number;
  code?: string;
  message?: string;
}
export interface Task<T> {
  taskId?: number;
  status?: number;
  creationTime?: string;
  result?: T;
}
export interface BaseParams {
  userId?: number;
  spaceId?: SpaceId;
  shareToken?: string;
  cancelToken?: CancelToken;
}

export interface DefaultUserOptions {
  allowPersonalSpace: boolean;
  enabled: boolean | string;
  personalSpaceQuotaSize: string;
}
export interface DefaultTeamOptions {
  defaultRoleId: number;
  spaceQuotaSize: string;
}

export enum EditionFlag {
  enterprise = 'enterprise',
  team = 'team',
  personal = 'personal',
}

export enum GroupRole {
  owner = 'owner',
  groupAdmin = 'groupAdmin',
  user = 'user',
}

export interface OrganizationItem {
  id: number;
  name: string;
  libraryId: LibraryId;
  isLastSignedIn?: boolean;
  ipLimitEnabled: boolean;
  orgUser?: {
    nickname: string;
    role: string;
    deregister: boolean;
    enabled: boolean;
  };
  extensionData: {
    editionConfig?: {
      editionFlag?: EditionFlag;
    };
    allowWeworkSelfApp: boolean;
    ensurePersonalSpace: boolean;
    logo: string;
    showOrgNameInUI: boolean;
    defaultUserOptions: DefaultUserOptions;
    defaultTeamOptions: DefaultTeamOptions;
  };
}

export interface AuthorityList {
  canAuthorize: boolean;
  canDelete: boolean;
  canDownload: boolean;
  canModify: boolean;
  canPreview: boolean;
  canShare: boolean;
  canUpload: boolean;
  canView: boolean;
  canPreviewSelf?: boolean;
  canDownloadSelf?: boolean;
}

export interface LocalSyncInfo {
  syncId: number;
  strategy: 'local_to_cloud';
  isSyncRootFolder: boolean;
  syncUserId: string;
}

export enum VirusAuditStatus {
  None = 0, // 未审核
  Auditing = 1, // 审核中
  Normal = 2, // 审核通过，无风险
  Block = 3, // 审核未通过，风险文件
  PreInvalid = 4, // 预判无法审核
  ManualPassed = 5, // 认为标记无风险
  PostFailed = 6, // 检测失败
}

export enum SensitiveWordAuditStatus {
  None = 0, // 未审核、无需审核
  Passed = 1, // 审核通过，未匹配敏感词，或企业无敏感词
  Fail = 2, // 审核未通过，匹配敏感词
  ManualPassed = 3, // 手动标记无风险
}
/**
 * @description 基础文件/文件夹所属类型
 */
export enum FileItemBaseType {
  dir = 'dir',
  file = 'file',
  image = 'image',
  video = 'video',
}

export type FileItemType = FileItemBaseType | string;

export enum SpaceTag {
  personal = 'personal',
  team = 'team',
  group = 'group',
}

/**
 * @description 团队空间基础信息
 */
export interface TeamInfoBaseData {
  id: number;
  name: string;
  spaceId: SpaceId;
  parentId: number;
  orgId: number;
  path: string[];
}

/**
 * @description 用户基础信息
 */
export interface UserInfoBaseData {
  id: number;
  name: string;
  orgId: number;
  spaceId: string;
}

/**
 * @description 用户头像展示基本信息(头像,昵称,ID)
 */
export interface UserInfoAvatarData {
  id: number;
  nickname: string;
  avatar: string;
}

/**
 * @description 协作群组基础信息
 */
export type GroupInfoBaseData = UserInfoBaseData & {
  userId?: number;
};

/**
 * @description 基础文件/文件夹项类型
 */
export interface FileItemBaseData {
  [key: string]: any;
  name: string; // 字符串，目录或相簿名或文件名；
  path: string[];
  type: FileItemType; // 字符串，条目类型：
  modificationTime: string;
  contentType: string;
  size: string;
  eTag: string;
  crc64: string;
  authorityList: AuthorityList;
  fileType: string;
  previewAsIcon?: boolean;
  previewByCI?: boolean;
  previewByDoc?: boolean;
  localSync?: LocalSyncInfo;
  hasSync?: boolean;
}

/* ***************************** */
/* ***** SMH Class 相关定义 ***** */
/* ***************************** */
export interface UserInfo {
  userId?: number;
  userToken?: string;
  userTokenExpiresIn?: number;
  userTokenStart?: number;
  organizationId?: number;
  libraryId?: LibraryId;
  isNewUser?: 'unknown' | 'no' | 'yes' | string;
}
export interface VistorInfo {
  userTokenExpiresIn?: number;
  userTokenStart?: number;
  libraryId?: LibraryId;
}

export type UserInfoParams = Partial<UserInfo>;

/* ***************************** */
/* ***** ATM Class 相关定义 ***** */
/* ***************************** */
/**
 * 基础空间类型
 * @description 后端返回的空间类型
 */
export interface BaseToken {
  accessToken: AccessToken;
  expiresIn: ExpiresIn;
}
export interface WeakToken extends BaseToken {
  libraryId: LibraryId;
  updateTime: number;
}
/**
 * 标准空间登录态类型
 * @description 在基础空间类型是维护一个 updateTime（秒）
 */
export interface SpaceToken extends BaseToken {
  spaceId: SpaceId;
  libraryId: LibraryId;
  updateTime: number;
  spaceOrgId?: number;
}
export interface SpaceBaseInfo {
  libraryId: LibraryId;
  spaceId: SpaceId;
}

export type RequestResponse<T> = Promise<AxiosResponse<T>>;

export type FetchTokenRequest = ((params: { spaceId: SpaceId }) => RequestResponse<any>) | null;

export interface AccessTokenManagerHttpParams {
  userToken?: UserToken;
  organizationId?: OrganizationId;
  baseURL?: string;
  envType?: 'web' | 'node';
}

export interface AccessTokenManagerHttpBody {
  spaceOrgId?: number;
}

export interface AccessTokenManagerBaseInfo {
  spaceId: SpaceId;
}

export interface AccessTokenManagerStorage<T = WeakToken> {
  get: () => Promise<Record<string, T>>;
  set: (spaceMap: Record<string, T>) => void;
  del: () => void;
}

export interface AccessTokenManagerConfig<T = WeakToken> {
  httpParams?: AccessTokenManagerHttpParams;
  storage: AccessTokenManagerStorage<T>;
}

export interface SpacePathInfo {
  spaceId: SpaceId;
  path: string;
}

/* ************************* */
/* 请求函数的入参及返回值类型定义 */
/* ************************* */

/**
 * 所有请求函数入参的父类
 */
export interface RequestParams {
  [key: string]: any;
}

/**
 * 所有请求函数返回值的父类
 */
export interface RequestData {
  [key: string]: any;
}

/** 表格后台分页通用  */
export interface ListTableParams {
  page: number;
  pageSize: number;
  keyword?: string;
  orderBy?: string;
  orderByType?: string;
}

export interface MiniListTableData<T> {
  contents: T[];
  totalNum: number;
}

export interface ListTableData<T> {
  contents: T[];
  page: number;
  pageSize: number;
  totalNum: number;
}

// 组织信息
export interface OrgUser {
  nickname: string;
  role: string;
  deregister: boolean;
  enabled: boolean;
  needChangePassword: boolean;
}

export interface OrgItem {
  id: number;
  name: string;
  isLastSignedIn?: boolean;
  libraryId: string;
  expired: boolean;
  ipLimitEnabled: boolean;
  orgUser?: OrgUser;
  extensionData: {
    allowWeworkSelfApp?: boolean;
    ensurePersonalSpace?: boolean;
    logo?: string;
    showOrgNameInUI?: boolean;
    defaultUserOptions?: DefaultUserOptions;
    defaultTeamOptions?: DefaultTeamOptions;
    enableWeworkLogin?: boolean;
    enableShare?: boolean;
    enableDocPreview?: boolean;
    enableDocEdit?: boolean;
    editionConfig?: {
      editionFlag: string;
      enableOverseasPhoneNumber?: boolean;
    };
    channelFlag?: string;
    isUpdated?: boolean;
  };
  isTemporary?: boolean;
}

// 列出当前登录用户所属组织
export type GetUserOrgData = OrgItem[];

export interface DomainItem {
  domain: string;
  isCustom: boolean;
  status: number;
}

export interface WatermarkConfig {
  enablePreviewWatermark?: boolean; // 预览水印是否开启
  previewWatermarkType?: number; // 预览水印类型
  enableDownloadWatermark?: boolean; // 下载水印是否开启
  downloadWatermarkType?: number; // 下载水印类型
  enableShareWatermark?: boolean; // 外链水印是否开启
  shareWatermarkType?: number; // 外链水印类型
}

export interface EditionOrgConfig {
  editionFlag: EditionFlag; // personal 个人版 | team 团队版 | enterprise 企业版
  userCountLimit: number; // 企业用户上限
  quotaLimit: number; // 企业存储空间上限，1GB
  enableTeamSpace: boolean; // 是否允许团队空间，企业空间
  shareSpaceLimit: number; // 共享空间数量上限
  shareSpaceUserCountLimit: number; // 共享空间人数上限
  enableShareSpaceDynamics: boolean; // 是否允许共享空间动态
  enableSyncDirectory: boolean; // 是否允许同步盘
  enableManageShareDirectory: boolean; // 是否可进行外链管理，外链次数限制 or 查看已分享的外链
  shareLinkLimit: number; // 外链条数限制
  enableManageWatermark: boolean; // 是否可管理水印配置
  watermarkDefaultText: string; // 固定水印信息
  enableDirectoryHistory: boolean; // 是否可后台管理历史版本
  restoreRecycledDays: number; // 可恢复回收站天数，3 天之内可恢复，3 天之外不可恢复，但在列表页可见
  enabledResignationTransfer: boolean; // 是否可以离职转接
  roleList: number[]; // 可用权限 ID
  enableLog: boolean; // 是否允许管理操作日志
  enableStatisticalReport: boolean; // 是否允许查看统计报表
  enableTeamAdmin: boolean; // 是否允许团队管理员（多级管理员设置）
  enableWechatIntegration: boolean; // 是否允许企业微信导入
  enableManageAntivirus: boolean; // 是否有安全扫描模块
}

// 查询组织信息
export interface GetOrgInfoData {
  bussiness: string;
  expired: boolean;
  id: number;
  name: string;
  domains: DomainItem[];
  extensionData: {
    allowWeworkSelfApp: boolean;
    logo?: string;
    ensurePersonalSpace?: boolean;
    showOrgNameInUI: boolean;
    watermarkOptions?: WatermarkConfig;
    enableShare?: boolean;
    allowChangeNickname: boolean; // 是否允许修改昵称
    enableViewAllOrgUser: boolean; // 是否显示企业的组织架构（隐藏后只能看到自己加入的团队和组织，只能精确搜索）
    enableOpenLdapLogin?: boolean;
    enableWindowsAdLogin?: boolean;
    enableWeworkLogin?: boolean;
    enableYufuLogin?: boolean;
    enableSensitiveWord?: boolean;
    enableGlobalFullTextSearch?: boolean;
    ssoWay?: string;
    syncWay?: string;
    showWeworkAppLink?: boolean;
    defaultUserOptions: DefaultUserOptions;
    defaultTeamOptions: DefaultTeamOptions;
    expireTime: string;
    editionConfig: EditionOrgConfig;
    channelFlag?: string;
    isUpdated?: boolean;
    isAccountNotDependentOnPhone: boolean; // 是否允许不绑定手机号
  };
}

// 组织logo信息
export interface OrgLogoInfo {
  orgLogo: string;
  orgName: string;
  showOrgNameInUI: boolean | undefined;
}

// 查询组织空间使用量
export interface OrgSpaceData {
  capacity: string;
  availableSpace: string;
  totalAllocatedSpaceQuota: string; // 已分配出去的配额
  totalFileSize: string; // 文件真实占用大小
}

// 获取指定空间信息和访问令牌
export interface GetSpaceInfoData {
  libraryId: string;
  spaceId: SpaceId;
  accessToken: string;
  expiresIn: number;
}

// 获取游客信息和访问令牌
export interface GetVistorInfo {
  libraryId: string;
  spaceId: SpaceId;
  vistorAccessToken: string;
  expiresIn: number;
}

// 列出个人空间
export interface HasPersonalSpaceData {
  hasPersonalSpace: boolean;
  capacity: string | null;
  availableSpace: string | null;
  size: string;
}

// 列出团队空间
export interface QueryTeamSpace {
  libraryId: string;
  spaceId: string;
  capacity: string;
  availableSpace: string;
  size: string;
}

// 列出团队空间
export interface TeamSpace {
  libraryId: string;
  spaceId: SpaceId;
  capacity: string | number;
  availableSpace: string | number;
  size: string;
  shareLibraryQuota?: boolean;
}
// 列出团队空间对应的内容
// export interface TeamSpaceDynamic {

// }

// 获取企业Logo简单上传文件参数
export interface PutAvatarData {
  domain: string;
  path: string;
  headers: {};
}

export type PutOrgLogoData = PutAvatarData;

// 确认上传企业Logo
export interface PostOrgLogoData {
  logo: string;
}

/** 分享模块 start */
export interface AddFavoriteParams {
  spaceId: string;
  path: string;
  favoriteGroupId?: number;
  spaceOrgId: number;
}
export interface DirectoryInfo extends AddFavoriteParams {
  versionId?: number;
  type: string;
}
export interface ShareLinkBaseInfo {
  expireTime: string;
  isPermanent: boolean;
  extractionCode?: string;
  linkToLatestVersion?: boolean;
  canModify?: boolean;
  canPreview?: boolean;
  canDownload?: boolean;
  canSaveToNetDisc?: boolean;
  previewCount?: number;
  downloadCount?: number;
  watermarkText?: string;
  shareTraffic: number;
}
export interface UpdateSharedLinkParams extends ShareLinkBaseInfo {
  shareId: number;
}

export interface GetShareLinkLogData {
  id: number;
  operationType: number; // 数字型 ，1预览 2下载 3转存
  operationContent: string; // 文件名
  deviceName: string; // 设备名
  ip: string;
  operationTime: string;
  userPhoneNumber?: string;
  type?: string;
  fileType?: string;
}

export interface GetShareLinkData {
  id: number;
  name: string;
  code: string;
  url: string;
  endpoint?: string;
  expireTime: string;
  isPermanent: boolean;
  creationTime: string; // 创建时间
  modificationTime?: string; // 修改时间
  type?: string;
  fileType?: string;
  fileName: string; // 此条分享链接文件列表中的第一个文件名
  fileNum: number; // 此条分享链接分享的文件数
  disabled?: boolean;
  canDownload?: boolean;
  canPreview?: boolean;
  downloadCount?: number;
  downloadCountUsed?: number;
  previewCount?: number;
  previewCountUsed?: number;
  shareTraffic: number;
  fileSpaceId?: string;
  fileSpaceTag?: string;
  filePath?: string[];
  nickname: string;
  userPhoneNumber: string;
  userCountryCode: string;
  userAccountName?: string;
  userId: number;
  isTemporary: boolean; // 分享人是否为临时用户
  domains: DomainItem[];
}
export interface OrgShareListParams {
  name?: string;
  expireTimeStart?: string;
  expireTimeEnd?: string;
  expireTimeRange?: string[];
  userFilter?: string;
  page?: number;
  pageSize?: number;
  orderBy?: string;
  orderByType?: string;
  groupId?: number;
}

export interface GetSharedLinkListData {
  totalNum: number;
  contents: GetShareLinkData[];
}

export interface GetSharedLinkLogListData {
  totalNum: number;
  logs: GetShareLinkLogData[];
}

export interface GetSharedLinkLogListParams {
  page: number;
  pageSize: number;
  shareId: number;
}

export interface ShareLinkFilesInfo {
  name: string;
  directoryInfoList: DirectoryInfo[];
}

export type GetShareLinkParams = ShareLinkFilesInfo & ShareLinkBaseInfo;

export type GetSingleFileShareLinkParams = {
  name: string;
  directoryInfo: DirectoryInfo;
  pageType: 'doc' | 'edit' | 'media' | string;
} & ShareLinkBaseInfo;
export interface GetSharedLinkItemData extends GetShareLinkParams {
  name: string;
  expireTime: string;
  id: number;
  url?: string;
  endpoint?: string;
  creationTime?: string;
  needExtractionCode?: boolean;
  userAvatar?: string;
  userNickname?: string;
  domains: DomainItem[];
}
export interface GetSharedLinkDetailData {
  name: string;
  expireTime: string;
  needExtractionCode: boolean;
  userId: number;
  userAvatar: string;
  userNickname: string;
  enableShareWatermark: boolean;
  shareWatermarkType: number;
  watermarkText: string;
  orgName: string;
  orgLogo: string;
  showOrgNameInUI: boolean;
  phoneNumberSuffix: string;
}
export interface SaveShareCodeParams {
  accessToken?: AccessToken;
  userToken?: UserToken;
  paths: string[];
  items: string[];
  period?: number;
}
/** 分享模块 end */

/** 获取空间动态 start */

// 返回的文件操作具体详细类型
type ActionTypeDetail =
  | 'download'
  | 'preview'
  | 'delete'
  | 'move'
  | 'rename'
  | 'copy'
  | 'create'
  | 'update'
  | 'restore';

export interface GetSpaceDynamicParams {
  spaceOrgId: number;
  spaceId?: string;
  actionTypeDetail?: ActionTypeDetail;
  startTime: string;
  endTime: string;
  groupBy?: 'operationTime';
  dirPath?: string;
}
// 获取空间动态参数
export interface GetSpaceDynamicMoreParams {
  searchId: string;
  spaceOrgId: number;
  marker?: number;
  groupBy?: 'operationTime';
}

export interface GetWorkbenchDynamicParams {
  actionTypeDetail?: ActionTypeDetail;
  startTime: string;
  endTime: string;
  onlyGroup?: boolean;
}

// 返回的空间动态数据 item节点类型
export interface DynamicData {
  id: number;
  logType: string;
  operationTime: string;
  action: string;
  actionType: string;
  spaceId: string;
  spaceTag: SpaceTag;
  operatorId: string;
  operatorName: string;
  operatorPhoneNumber: string;
  operatorCountryCode: string;
  operatorAccountName?: string;
  isExternal: boolean;
  objectId: string;
  objectPath: string;
  objectName: string;
  details: string;
  IP: string;
  device: string;
  attachInfo: { operatorPhoneNumber: string };
  actionTypeDetail: ActionTypeDetail;
  isFile: boolean;
  toObjectName: string;
  toObjectPath: string;
  toParentId: number;
  contentType: string;
  IpLocation: string;
  apiLogId: number;
  orgId: number;
  type: string;
  actionName?: string;
  team?: TeamInfoBaseData;
  user?: UserInfoBaseData;
  group?: GroupInfoBaseData;
  isUserBelongSpace?: boolean;
}

export interface DynamicDataContents {
  id?: number;
  list: DynamicData[];
  date: string;
}

// 我的工作台，动态节点数据
export interface WorkTeam {
  id: number;
  name: string;
  spaceId: string;
  parentId: number;
  orgId: number;
  path: string[];
}

export interface WorkDetails {
  id: number;
  logType: string;
  operationTime: string;
  action: string;
  actionType: string;
  libraryId: string;
  spaceId: string;
  spaceTag: SpaceTag;
  operatorId: string;
  operatorName: string;
  objectPath: string;
  objectName: string;
  details: string;
  actionTypeDetail: string;
  isFile: boolean;
  toObjectName: string;
  toObjectPath: string;
  toParentId: number;
  contentType: string;
  orgId: number;
  orgName: string;
  isExist: boolean;
  operatorAvatar: string;
  operatorPhoneNumber: string;
  fileType: string;
  type: string;
  previewAsIcon: boolean;
  previewByCI: boolean;
  previewByDoc: boolean;
  team: WorkTeam;
  actionName: string;
}

export interface WorkData {
  name: string;
  details: WorkDetails[];
  tag: string;
}

// 返回的空间动态数据类型
export interface GetSpaceDynamicData {
  searchId: string;
  hasMore: boolean;
  searchFinished: boolean;
  total: { value: number; relation: string };
  nextMarker: number;
  contents: DynamicDataContents[];
  lastHit: DynamicData;
}
/** 获取空间动态 end  */

export interface SaveShareCodeData {
  code: string;
}
export interface CheckCodeVisitData {
  visits: number;
}

export interface VerifyExtractionCodeParams {
  shareToken: string;
  extractionCode: string;
  deviceId: string;
  userToken?: string;
  captchaTicket?: string;
  captchaRandstr?: string;
}
export type VerifyExtractionCodeData = GetSpaceInfoData;
export interface GetSingleFileShareLinkData {
  url: string;
  expireTime: string;
}

/** 企微配置 start */
export interface WeworkConfigParams {
  corpid?: string;
  agentId?: number;
  secret?: string;
  autoOrgSyncEnabled?: boolean;
}

export type UpdateWeworkConfigParams = WeworkConfigParams;

export type LoginConfigData = WeworkConfigParams;

export interface GetWeworkDelegateQrcodeData {
  qrcodeUrl: string;
  expiresIn: number;
}

export interface GetDelegateInfoData {
  name: string;
  roundLogo: string;
  squareLogo: string;
}

export type SyncType = 'SyncWeworkOrg' | 'SyncYufuOrg' | 'SyncAccountOrg' | string;

export interface SyncWeworkOrgResultData {
  // 返回部分成功信息
  weworkUserCount?: number;
  weworkTeamCount?: number;
  failedItems: Array<{
    type: string;
    synced: boolean;
    code: string;
    // 用户信息
    departmentPath: number;
    name: string;
    countryCode: string;
    phoneNumber: string;
    // 团队信息
    weworkDepartmentName: string;
    weworkDepartmentId: number;
  }>;
  // 直接返回失败
  // code: string;
  // message: string;
  // status: number;
}
/** 企微配置 end */

/** iDaas配置 start */
export interface IDaasServiceAccountParams {
  clientId?: string;
  privateKey?: any;
}
export interface IDaasConfigParams {
  tenantName: string;
  type?: 'sso' | 'sync';
  clientId?: string;
  clientSecret?: string;
  wellKnown?: string;
  serviceAccount?: IDaasServiceAccountParams | string;
  autoOrgSyncEnabled?: boolean;
  callbackUrls?: Array<string>;
  oauthUrl?: string;
}
export type UpdateIDaasConfigParams = IDaasConfigParams;
export interface SyncOrgResultData extends SyncWeworkOrgResultData {
  yufuUserCount?: number;
  yufuTeamCount?: number;
  departmentCount?: number;
  userCount?: number;
}
/** iDaas配置 end */

/** 统一认证登录配置 start */

export interface CasProtocol {
  redirectUri: string;
  ssoEndpoint: string;
  loginFlag: string;
  userInfoEndpoint: string;
  isCreateAccountOnLogin: boolean; // 无同步账号时是否开启自动创建账号，默认false
}
export interface OauthProtocol {
  redirectUri: string;
  clientId: string;
  clientSecret: string;
  ssoEndpoint: string;
  tokenEndpoint: string;
  userInfoEndpoint: string;
  loginFlag: string;
  scopeRange?: Array<string>;
  isCreateAccountOnLogin: boolean;
}

export interface OidcProtocol {
  redirectUri: string;
  clientId: string;
  clientSecret: string;
  ssoEndpoint: string;
  tokenEndpoint: string;
  loginFlag: string;
  jwksUri?: string;
  jwtSecret?: string;
  isCreateAccountOnLogin: boolean;
}

export interface OpenLdapProtocol {
  url: string;
  bindDN: string;
  bindPassword: string;
  searchDN: string;
  departmentObjectclass: string;
  userObjectclass: string;
  loginFlag: string;
  loginFlagShow: string;
}

export interface OpenLdapProtocolFormData {
  type: 'openLdap' | 'windowsAd' | string;
  protocol: 'ldap' | 'ldaps' | string;
  hostname: string;
  port: string;
  bindDN: string;
  bindPassword: string;
  searchDN: string;
  departmentObjectclass: string;
  userObjectclass: string;
  loginFlag: string;
  loginFlagShow: string;
}

export interface SupwisdomProtocolParams {
  appId: string;
  appSecret: string;
  dataEndpoint: string;
  tokenEndpoint: string;
  userInterFaceId: string;
  departmentInterfaceId: string;
  availableDepartmentId: string;
  availableStatus: string;
  isHideAvailableDepartmentId?: boolean;
}

export interface ProtocolParams {
  protocol?: 'cas' | 'oauth' | 'oidc' | 'openLdap' | 'windowsAd' | string;
}
export interface ProtocolData {
  config?: CasProtocol | OauthProtocol | OidcProtocol | OpenLdapProtocol | SupwisdomProtocolParams;
}

export interface AccountProtocolParams extends ProtocolParams, ProtocolData {
  autoOrgSyncEnabled?: boolean;
}

export interface AccountTestResultData {
  departmentCount: number;
  userCount: number;
  departments: {
    status: string;
    length: number;
  };
  users: {
    status: string;
    length: number;
  };
}

export interface AccountParams {
  autoOrgSyncEnabled: boolean;
  configList: AccountProtocolParams[];
  ssoWay: 'cas' | 'oauth' | 'oidc' | 'yufu';
  syncWay: 'wework' | 'yufu' | 'openLdap' | 'windowsAd';
}
/** 统一认证登录配置 end */

/** 统计模块 start  */
export interface UserCountData {
  userLimit: number;
  currentUser: number;
  temporaryUser: number;
  disabledFormalUser: number;
  disabledTemporaryUser: number;
  onlineUser: number;
}
export interface GetSpaceStatsParams {
  spaceTag: 'team' | 'personal' | '';
}
export type SpaceStatsData = { size: string; spaceTag: SpaceTag }[];
export interface GetSpaceUsedInfoParams {
  page: number;
  pageSize: number;
  keyword: string;
  orderBy: string;
  orderByType: string;
}
export interface SpaceUsedInfoData<T> {
  page: number;
  pageSize: number;
  totalNum: number;
  contents: T[];
}
export interface PathNodes {
  id: number;
  name: string;
}
export interface TeamSpaceUsedInfoData {
  availableSpace: string;
  capacity: string;
  id: number;
  internetTraffic: number;
  name: string;
  parentId: number;
  path: string[];
  pathNodes: PathNodes[];
  size: string;
  spaceId: string;
  userCount: number;
}
export interface PersonalSpaceUsedInfoData {
  allowPersonalSpace: string;
  availableSpace: string;
  capacity: string;
  countryCode: string;
  enabled: string;
  id: number;
  internetTraffic: number;
  nickname: string;
  phoneNumber: string;
  accountName: string;
  role: string;
  size: string;
}
export interface TrafficUsageOverviewData {
  freeRemain: string;
  freeTotal: string;
  paidRemain: string;
  paidTotal: string;
}
export interface TrafficUsageTimingParams {
  startTime: string;
  endTime: string;
}
export interface TrafficDataPoint {
  metricLabel: string;
  metricName: string;
  values: number[];
}
export interface TrafficUsageTimingData {
  startTime: number;
  endTime: number;
  timestamps: number[];
  datapoints: TrafficDataPoint[];
}
/** 统计模块 end  */

/** 消息列表 start  */
export interface ListMessageParams {
  type: number | string;
  readState?: number | string;
  page?: number;
  pageSize?: number;
}

// 消息列表内容详情
export interface MessageItem {
  id: number;
  title: string;
  internationalTitle: { [key: string]: string }; // 多语言标题，结构为 { 'zh-CN': 'xxx', 'en-US': ...}
  content: string;
  contentTemplate: string; // 替换模板，用于解决内容过长的缩略问题 e.g: '${fileName} 被共享给用户 ${userName}'
  internationalTemplate: { [key: string]: string }; // 多语言替换模版，结构为 { 'zh-CN': '${fileName} 被共享给用户 ${userName}', 'en-US': ...}
  templateData: { [key: string]: string }; // 用于替换模板的数据 e.g: {fileName:'测试.doc',userName:'张三'}
  internationalTemplateData: { [key: string]: { [key: string]: string } }; // e.g: { 'zh-CN': {fileName:'测试.doc',userName:'张三'},  'en-US': ...}
  notifiedBy: string;
  notifiedByNickname: string;
  notifiedByAvatar: string;
  toUserId: string;
  toUserNickname: string;
  toUserAvatar: string;
  type: number; // 大类别
  hasRead: boolean;
  creationTime: string;
  // authorityAndSettingMsg: 权限消息, shareMsg: 外链消息, esignMsg: 电子签消息, userManageMsg:用户消息, quotaAndRenewMsg:扩缩容消息, securityMsg:安全扫描消息;
  typeDetail: 'authorityAndSettingMsg' | 'shareMsg' | 'esignMsg' | 'userManageMsg' | 'quotaAndRenewMsg' | 'securityMsg';
  iconType: 'add-circle' | 'warn-circle' | 'minus-circle'; // warn-circle（感叹号）、add-circle（加号）、minus-circle（减号）
  linkName: string; // 动作点的展示文案
  internationalLinkName: { [key: string]: string }; // 多语言动作点的展示文案，结构为 { 'zh-CN': '立即前往', 'en-US': ...}
  linkType: 'outside' | 'inside-user-management' | 'show-virus-file' | string; // outside（跳外部链接）、inside-user-management（跳后台管理）、show-virus-file（跳病毒文件展示）
  linkUrl: string; // 外部链接才返回
  securityPath: string[]; // 安全扫描才返回
}
export interface ListMessageData {
  totalNum: number; // 单个分类的总数
  unreadMessageNumber: number;
  unreadSystemMessageNumber: number;
  unreadWarnMessageNumber: number;
  contents: MessageItem[];
  allMessageNum: number; // 总的消息数
}

export interface ReceiveMessageConfig {
  authorityAndSettingMsg: boolean; // 是否开启权限消息
  shareMsg: boolean; // 是否开启分享消息
  esignMsg: boolean; // 是否开启电子签消息
  securityMsg: boolean; // 是否开启安全扫描消息
  userManageMsg: boolean; // 是否开启用户管理消息
  quotaAndRenewMsg: boolean; // 是否开启文件配额消息
}

export interface SettingConfigData {
  receiveMessageConfig: ReceiveMessageConfig;
}

export interface MessageInfo {
  type: number;
  messageIdList: number[];
}
/** 消息列表 end  */

export interface getDirectoryOrFileInfoParams {
  path: string;
  spaceOrgId: number;
  spaceId?: string;
  syncId?: number;
}
// 获取文件或目录的详细信息
export interface getDirectoryOrFileInfoData extends FileItemBaseData {
  organizationName: string;
  creationPerson: string;
  creationPersonId: number;
  creationPersonOrgId: number;
  creationPersonAccountName: string;
  creationPersonPhoneNumber: string;
  creationPersonCountryCode: string;
  isExternal: boolean;
  creationTime: string;
  inDirectory: boolean;
  inRecycledDirectory: boolean;
  isAuthorized: boolean;
  historySize: number;
  modificationTime: string;
  spaceTag: SpaceTag; // personal: 个人空间， team: 企业空间, group: 协作空间;
  user?: UserInfoBaseData;
  team?: TeamInfoBaseData;
  group?: GroupInfoBaseData;
  authorityList: AuthorityList;
  virusAuditStatus: VirusAuditStatus;
  virusName?: string;
  virusType?: string;
  tagList: string[];
  sensitiveWordAuditStatus: SensitiveWordAuditStatus;
  sensitiveWordList: string[];
  canMarkAsNonSensitiveWord?: boolean; // 敏感词是否可信任
  canMarkAsNonVirus?: boolean; // 病毒是否可信任
}

/** 用户列表模块 start  */
export interface CustomRole {
  id?: number;
  name: string;
  canAuthorize: boolean;
  canDelete: boolean;
  canDownload: boolean;
  canDownloadSelf: boolean;
  canModify: boolean;
  canPreview: boolean;
  canPreviewSelf: boolean;
  canPrint: boolean;
  canShare: boolean;
  canUpload: boolean;
  canView: boolean;
  roleDesc?: string;
}

export interface GetExtUserListParams extends ListTableParams {
  orderBy?: string;
  orderByType?: string;
  showPhoneNumber?: boolean;
}

export interface Role extends CustomRole {
  id: number;
  isOwner: boolean;
  isDefault: boolean;
  isCustom: boolean;
  roleDesc: string;
}
export interface UserBaseInfo {
  accountName: string;
  id: number;
  countryCode: string;
  phoneNumber: string;
  avatar: string;
  nickname: string;
  email: string;
  comment: string;
  role: string;
  enabled: boolean;
  allowPersonalSpace: boolean;
  personalSpaceQuotaSize: string;
  hasPersonalSpace?: boolean;
  lastSignedInOrgId?: number;
  capacity?: string | null;
  availableSpace?: string | null;
  hasLoggedIn?: boolean | string;
  teams?: string[];
  canManageTeams?: number[];
  inactive?: boolean;
  within7DaysOrgUser: boolean;
  isNewUser: boolean;
  thirdPartyAuthList?: {
    meeting: boolean;
    wechat: boolean;
  };
  hasAppliedChannelOrg?: boolean;
  isFirstLogin?: boolean;
  isTemporary?: boolean;
}
export interface GetUserListParams extends ListTableParams {
  withBelongingTeams?: boolean;
  withSpaceUsage?: boolean;
  showPhoneNumber?: boolean;
  orderBy?: string;
  orderByType?: string;
  includeInactiveUser?: number;
}
export interface GetExtUserListParams extends ListTableParams {
  orderBy?: string;
  orderByType?: string;
}
export interface UserListItemData {
  allowPersonalSpace: boolean;
  availableSpace: string;
  avatar: string;
  capacity: string;
  comment: string;
  countryCode: string;
  email: string;
  enabled: boolean;
  expireTime: string | null;
  id: number;
  nickname: string;
  orgId: number;
  phoneNumber: string;
  role: string;
  canManageTeams?: number[];
  accountName: string | null;
}
export interface ExtUserListItemData {
  id: number;
  orgId: number;
  countryCode: string;
  phoneNumber: string;
  nickname: string;
  comment: string;
  enabled: boolean;
  avatar: string;
  userId: number;
  name: string;
  added?: boolean;
  newAdd?: boolean;
}
export type GetUserListData = ListTableData<UserListItemData>;
export type GetExtUserListData = ListTableData<ExtUserListItemData>;
export interface CreateUserParams {
  accountName?: string;
  accountPassword?: string;
  countryCode?: string;
  phoneNumber?: string;
  email?: string;
  nickname: string;
  comment: string;
  allowPersonalSpace: boolean;
  personalSpaceQuotaSize: string;
  role: string;
  enabled: boolean;
}
export interface CreateExtUserParams {
  countryCode: string;
  phoneNumber: string;
  nickname: string;
  comment: string;
  enabled: boolean;
}
export interface CreateUserData {
  id: number;
}
export interface CreateExtUserData {
  userId: number;
}
export interface UpdateUserParams {
  userId: number;
  accountName?: string;
  countryCode?: string;
  phoneNumber?: string;
  email?: string;
  nickname?: string;
  comment?: string;
  allowPersonalSpace?: boolean;
  personalSpaceQuotaSize?: string;
  role?: string;
  enabled?: boolean;
}
export interface UpdateExtUserParams {
  userId: number;
  nickname?: string;
  comment?: string;
}
export interface DeleteUsersTransferParams {
  dist_user_id?: number;
  move_space?: number;
  move_share?: number;
  move_created_group_authorization?: number;
  move_join_group_authorization?: number;
  move_authorized?: number;
  move_received_authorization?: number;
}
export interface DeleteUsersParams extends DeleteUsersTransferParams {
  userIds: string;
}
export interface PostAvatarData {
  avatar: string;
}

export interface GetUserOwnerGroupData {
  ownerId: number;
  count: number;
}

export interface UserCapacityDataDetail {
  userId: number;
  groupSaceSize: number;
  personSpaceSize: number;
}

export interface UserCapacityData {
  groupSaceSizes: number;
  personSpaceSizes: number;
  detail: UserCapacityDataDetail[];
}

/** 用户列表模块 end  */

/** 团队管理模块 start  */
export interface TeamInfo {
  name: string;
  parentId?: number;
  parentName?: string;
  spaceQuotaSize: string;
  defaultRoleId: number;
  noSpace?: boolean;
}
export interface CreateTeamParams {
  name: string;
  parentId?: number;
  spaceQuotaSize?: string;
  defaultRoleId: number;
}
export interface CreateTeamData {
  id: number;
}
export interface UpdateTeamParams extends CreateTeamParams {
  teamId: number;
}
export interface DeleteTeamParams {
  teamId: number;
  moveSpaceToTeam: number | string;
  moveSpaceToDir: string;
}
export interface QueryTeamParams {
  teamId: number | string;
  checkPermission?: 0 | 1;
  checkManagementPermission?: 0 | 1;
  withPath?: boolean;
  withNoSpaceTeams?: 0 | 1;
  withActiveUserCount?: 0 | 1;
}
export interface TeamMemberItemData extends UserListItemData {
  teamRoleId?: string;
  teamRole?: string;
  inactive?: boolean;
  teams: {
    id: number;
    name: string;
  }[];
}
export interface QueryTeamData {
  children: QueryTeamData[];
  defaultRoleId: string;
  id: number;
  name: string;
  orgId: number;
  parentId: number;
  path: string[];
  pathNodes: PathNodes[];
  spaceId: string;
  userCount: number;
}
export type QueryTeamMemberData = ListTableData<TeamMemberItemData>;
export interface TeamTreeNode {
  name: string;
  id: TeamId;
  parentId?: TeamId;
  children?: TeamTreeNode[];
  orgId?: string | number;
  spaceId: SpaceId;
  userCount?: string | number;
  path?: string[];
  added?: boolean;
  newAdd?: boolean;
  userId?: number;
  inactive?: boolean;
  isTemporary?: boolean;
  uid?: string;
}
/** 团队管理模块 end  */

/** 收藏列表 start  */
export interface GetFavoriteListParams {
  page: number;
  pageSize?: number;
  orderBy: string | undefined;
  orderByType: string;
}

export interface FavoriteFileItemData extends FileItemBaseData {
  id: number; // 收藏 ID；
  spaceId: SpaceId;
  spaceOrgId: number;
  spaceTag: SpaceTag;
  visible: boolean; // 是否可见（文件目录被删除或无权限时为 false)；
  relatedToMePath: string[] | null;
  favoriteTime: string;
  team?: TeamInfoBaseData;
  group?: GroupInfoBaseData;
  user?: UserInfoBaseData;
}
export interface GetFavoriteListData {
  totalNum: number;
  contents: FavoriteFileItemData[];
}
export interface GetFileExtraInfoParams {
  spaceId: string;
  path: string;
  spaceOrgId: number;
}
export interface GetFileExtraInfoData {
  spaceId: string;
  path: string;
  isAuthorized: boolean;
  spaceOrgId?: number;
  favoriteGroupId?: number;
  // 云查杀状态
  virusAuditStatus: VirusAuditStatus;
  favoriteId?: number;
  // 是否可以权限申请
  canApply: boolean;
  hasApplied: boolean;
  sensitiveWordAuditStatus: any;
}
export interface GetImageThumbnailResult {
  totalNum: number; // 总数
  contents: ImageThumbnailData[]; // 内容
}
export interface ImageThumbnailData {
  cosUrl: string; // 图片链接
}
export interface AddFavoriteData {
  id: number;
}
/** 收藏列表 end  */

/** 最近使用文件列表 end  */
export interface RecentlyUsedFileItemData extends FileItemBaseData {
  spaceId: SpaceId;
  spaceOrgId: number;
  spaceTag: SpaceTag;
  visible: boolean;
  relatedToMePath: string[] | null;
  team?: TeamInfoBaseData;
  group?: GroupInfoBaseData;
  user?: UserInfoBaseData;
}
export type GetRecentlyUsedDirectoryParams = ListTableParams;
export type GetRecentlyUsedDirectoryData = MiniListTableData<RecentlyUsedFileItemData>;
/** 最近使用文件列表 end  */

/** 与我相关列表 end  */
export interface RelatedToMeFileItemData extends FileItemBaseData {
  spaceId: string;
  description: string; // 与我相关动态列描述
  spaceTag: string;
  spaceOrgId?: number;
}
/** 与我相关列表 end */

/** 搜索日志模块 start  */
export type LogActionType = string;
// | 'Login'
// | 'UserManagement'
// | 'TeamManagement'
// | 'ShareManagement'
// | 'AuthorityAction'
// | 'FileAction'
// | 'SyncAction';
export type LogSpaceTag = 'all' | 'personal' | 'team' | '';

export interface ListSearchContentParams extends BaseParams {
  checkChildren?: boolean;
  searchId: string;
  spaceOrgId?: number;
  marker?: number;
  searchBy?: string;
  withNoSpaceTeams?: 0 | 1;
}
export interface CreateSearchLogTaskParams {
  keyword: string;
  actionType: LogActionType;
  startTime: string;
  endTime: string;
  operator: string;
  spaceTag: LogSpaceTag;
  teamId?: TeamId;
  searchAfterId?: {
    id: number;
    logType: string;
    operationTime: string;
  };
}
export interface DeleteSearchTaskParams extends BaseParams {
  searchId: string;
}
export type ListSearchLogParams = ListSearchContentParams;
export type DeleteSearchLogTaskParams = DeleteSearchTaskParams;

export interface SearchLogItem {
  id: number | string;
  logType: string;
  operationTime: string;
  action: string;
  orgId: number;
  operatorId: number;
  operatorName: string;
  modificationTime: string;
  operatorPhoneNumber: string;
  objectId: number;
  objectName: string;
  objectType: string;
  teamId: number;
  teamPath: string;
  details: string;
  IP: string;
  IpLocation: string;
  device: string;
  attachInfo: { operatorPhoneNumber: string };
}
export interface SearchLogResult {
  total: {
    value: number;
    relation: 'eq' | 'gte';
  };
}

export interface CreateDownloadLogTaskParams {
  data: {
    searchParams: {
      keyword: string;
      actionType: LogActionType;
      startTime: string;
      endTime: string;
      operator: string;
      teamId?: TeamId;
      spaceTag: LogSpaceTag;
    };
    targetSpaceId: SpaceId;
    targetDirectory: string;
  };
}

export interface ListDownloadLogTaskContentItem {
  taskId: number;
  action: string;
  status: number;
  total: number;
  remaining: number;
  label: string;
  creationTime: string;
  result: {
    path?: string[]; // 该字段仅在完成时存在
    spaceId?: SpaceId; // 该字段仅在完成时存在
    fileName?: string; // 该字段仅在完成时存在
    teamId?: TeamId; // 该字段仅在目标为团队空间时存在
    userId?: UserId; // 该字段仅在目标为个人空间时存在
    code?: string; // 该字段仅在进行时存在
    status?: number; // 该字段仅在进行时存在
    message?: string; // 该字段仅在进行时存在
    requestId?: string; // 该字段仅在进行时存在
    smhStatus?: number; // 该字段仅在进行时存在
  };
}

export interface ListDownloadLogTaskData {
  totalNum: number;
  page: number;
  pageSize: number;
  contents: ListDownloadLogTaskContentItem[];
}

/** 搜索日志模块 end  */

/** 协作群组模块 start  */
export type ColleborationSpaceId = number;
export interface ColleborationMember {
  userId: number;
  orgId: number;
  role: 'user' | 'groupAdmin' | string;
  authRoleId?: number;
}
export interface CreateColleborationSpaceParams {
  name: string;
  allowExternalUserData?: boolean;
  users?: ColleborationMember[];
  templateIds?: number[];
}
export interface ColleborationMemberDetail extends PersonalSpaceUsedInfoData {
  groupRole: 'user' | 'groupAdmin' | string;
  authRoleId: number;
  isExternal: boolean;
  avatar: string;
  orgId: number;
  orgName: string;
  isTemporary?: boolean;
}
export interface ListColleborationSpacesParams {
  page: number;
  pageSize: number;
  withDirectory?: number;
  withFileCount?: number;
  checkUpdateRecursively?: number;
  adminOnly?: number;
}

export interface GetColleborationMember {
  id: number;
  nickname: string;
  groupRole: 'owner' | 'user' | 'groupAdmin' | string;
  enabled: boolean;
  avatar: string;
}
export interface GetColleborationSpaceInfoData {
  id: number;
  orgId: number;
  orgName: string;
  name: string;
  spaceId: string;
  userId: number;
  extensionData: unknown;
  roleList: Array<Role>;
  owner: {
    id: number;
    orgId: number;
    nickname: string;
    avatar: string;
    isExternal: boolean;
  };
  userCount: number;
  isExternal: boolean;
  hasCrossOrgUser: boolean;
}
export interface ColleborationSpaceInfoFileItemData {
  name: string; // 字符串，目录或相簿名或文件名；
  type: FileItemType; // 字符串，条目类型：
  modificationTime: string;
  userId: number;
  userOrgId: number;
  fileType: string;
  modifierName: string;
  previewAsIcon?: boolean;
  previewByCI?: boolean;
  previewByDoc?: boolean;
  authorityList: AuthorityList;
}
export interface ColleborationSpaceInfoData extends GetColleborationSpaceInfoData {
  groupRole: GroupRole;
  id: number;
  users: GetColleborationMember[];
  fileCount: number;
  joinTime: string;
  modificationTime: string;
  ownerId: number;
  ownerName: string;
  orgEditionFlag: EditionFlag;
  orgExpired: boolean;
  creationTime: string;
  allowExternalUser: boolean;
  directoryList?: ColleborationSpaceInfoFileItemData[];
  authorityList?: AuthorityList;
}
export type ListColleborationSpaceInfoData = ListTableData<ColleborationSpaceInfoData>;
export interface GetCollaborationSpaceMembersParams {
  groupId: number;
  keyword?: string;
  page?: number;
  pageSize?: number;
  orderBy?: 'groupRole' | 'enabled' | 'nickname' | string;
  orderByType?: 'desc' | 'asc' | string;
  spaceOrgId?: number;
}
export interface GetCollaborationSpaceMembersData {
  groupId: number;
  keyword?: string;
  page?: number;
  pageSize?: number;
  orderBy?: 'groupRole' | 'enabled' | 'nickname' | string;
  orderByType: 'desc' | 'asc' | string;
}
export interface GenerateGroupInviteCodeParams {
  groupId: number;
  authRoleId: number;
}

export interface GenerateInviteCodeParams {
  role: 'user' | 'admin' | string;
  allowPersonalSpace?: boolean;
  personalSpaceQuotaSize?: string;
}
export interface UpdateInviteCodeParams extends Partial<GenerateGroupInviteCodeParams> {
  code: string;
  enabled?: boolean;
  expireTime?: string;
  allowPersonalSpace?: boolean;
  personalSpaceQuotaSize?: string;
}
export interface GetGroupInviteInfoData {
  id: number;
  orgId: number;
  userId: number;
  code: string;
  domains: DomainItem[];
  type: string;
  creationTime: string;
  expireTime: string;
  expired: boolean;
  authRoleId: number;
  organization: {
    name: string;
  };
  group: {
    id: number;
    name: string;
    orgId: number;
  };
  invitor: {
    id: number;
    orgId: number;
    avatar: string;
    nickname: string;
  };
  invitedCount: number;
  enabled: boolean;
  allowExternalUser: boolean;
  currentOrgUserCount: number;
  currentGroupUserCount: number;
  allowPersonalSpace: boolean;
  personalSpaceQuotaSize: string;
}
/** 协作群组模块 end  */

/** 电子签模块 start  */
export interface ElectronicSignTemplate {
  name: string;
  templateId: string;
}

export type GetElectronicSignTemplatesData = ElectronicSignTemplate[];

export interface GetElectronSignKeyParams {
  distDirectoryPath: string;
  distSpaceId: string;
  distLibraryId: string;
  originalPath: string;
  originalSpaceId: string;
  originalLibraryId: string;
  originalDocSaveInfo?: any;
}

export interface GetElectronSignStatusParams {
  key: string;
}

export interface GetElectronSignStatusData {
  status: 'new' | 'scanned' | 'needToConfirm' | 'confirmed' | 'codeReturned' | 'finished';
  key: string;
  expired: boolean;
  avatar: string;
  nickname: string;
}

export type ConfirmElectronSignKeyParams = GetElectronSignStatusParams;
/** 电子签模块 end  */

/** 租户空间 start  */
export interface GetSpaceConfigData {
  isPublicRead: boolean;
  allowPhoto: boolean;
  allowVideo: boolean;
  allowPhotoExtname: string[];
  allowVideoExtname: string[];
  recognizeSensitiveContent: boolean;
}
export interface GetSpaceSizeData {
  size: string;
}
export interface CreateSpaceData {
  spaceId: SpaceId;
}
/** 租户空间 end  */

/** 令牌 start  */
export interface RenewAccessTokenData {
  accessToken: string;
  expiresIn: number;
}
/** 令牌 end  */

// 保存至我的网盘
export interface BatchCopyItem {
  copyFromLibraryId?: string;
  copyFromSpaceId?: SpaceId;
  copyFrom: string;
  to: string;
  conflictResolutionStrategy?: string;
}
export interface SaveAsParams {
  spaceId: string;
  shareAccessToken: string;
}

/** 目录操作 start  */
export interface DirectoryParams extends BaseParams {
  spaceId: string;
  dirPath: string;
  syncId?: number; // 同步任务必须参数
  collectToken?: string;
}
export interface ListDirectoryParams extends DirectoryParams {
  nextMarker?: number;
  limit?: number;
  page?: number;
  pageSize?: number;
  orderBy?: string;
  orderByType?: string;
  filter?: string;
  sortType?: string;
  spaceOrgId?: number;
}

// 空间列表中的文件项类型
export interface SpaceFileItemData extends FileItemBaseData {
  creationTime: string;
  userId: string;
  virusAuditStatus?: VirusAuditStatus; // 只有分享外链中获取的文件列表才会返回该字段
  spaceOrgId?: number;
  sensitiveWordAuditStatus?: number;
  sensitiveWordList?: string[];
  isUserBelongSpace?: boolean; // 判断当前数据是否数据本企业空间
  // metaData?: any; // 当前还未返回此值
}
export interface ListDirectoryData {
  authorityList: AuthorityList;
  contents: SpaceFileItemData[];
  eTag: string;
  fileCount: number;
  totalNum: number;
  path: string[];
  subDirCount: number;
  localSync?: LocalSyncInfo;
  nextMarker?: number;
}
export interface CreateDirectoryParams extends DirectoryParams {
  conflictResolutionStrategy?: 'ask' | 'rename';
  collectToken?: string;
}
export type PathStringList = {
  [key: string]: string[];
} & {
  taskId?: number;
};
export interface DeleteDirectoryParams extends DirectoryParams {
  permanent?: number;
  spaceOrgId?: number;
}
export interface DeleteDirectoryData {
  recycledItemId: string;
}
export interface RenameOrMoveDirectoryParams extends DirectoryParams {
  from: string;
  conflictResolutionStrategy?: string;
  moveAuthority?: boolean;
  spaceOrgId?: number;
}
export interface RenameOrMoveDirectoryData {
  path: string[];
}
export interface CopyDirectoryParams extends DirectoryParams {
  conflictResolutionStrategy?: string;
  copyFrom: string;
}
export interface CrossSpaceCopyDirectoryParams extends CopyDirectoryParams {
  conflictResolutionStrategy?: string;
  copyFrom: string;
  copyFromSpaceId: string;
}
/** 目录操作 end  */

/** 文件操作模块 start  */
export interface FileParams extends BaseParams {
  filePath: string;
  contentDisposition?: 'inline' | 'attachment';
  historyId?: number;
  shareToken?: string;
  collectToken?: string;
  spaceOrgId?: number;
  sharePreviewerOrganizationId?: number;
  docPreviewMode?: string;
}
export interface InspectFileParams {
  path: string;
  spaceId: string;
}
export interface getDirectoryOrFileInfoParams {
  path: string;
  spaceOrgId: number;
  spaceId?: string;
  syncId?: number;
}
// 获取文件或目录的详细信息
export interface getDirectoryOrFileInfoData extends FileItemBaseData {
  organizationName: string;
  creationPerson: string;
  creationTime: string;
  inDirectory: boolean;
  inRecycledDirectory: boolean;
  isAuthorized: boolean;
  historySize: number;
  modificationTime: string;
  spaceTag: SpaceTag; // personal: 个人空间， team: 企业空间, group: 协作空间;
  user?: UserInfoBaseData;
  team?: TeamInfoBaseData;
  group?: GroupInfoBaseData;
  authorityList: AuthorityList;
  virusAuditStatus: VirusAuditStatus;
  virusName?: string;
  tagList: string[];
  sensitiveWordAuditStatus: SensitiveWordAuditStatus;
  sensitiveWordList: string[];
}
export interface GetFileInfoData extends FileItemBaseData {
  creationTime: string;
  cosUrl: string;
  cosUrlExpiration: string;
  ETag: string;
}
export interface GetCosUrlData extends GetFileInfoData {
  cosUrl: string;
  cosUrlExpiration: string;
  availableCosUrls?: string[];
}
export interface UploaFileParams extends FileParams {
  conflictResolutionStrategy?: string;
  filesize?: number;
  fullHash?: string;
  beginningHash?: string;
  size?: string;
  partNumberRange?: string;
  collectToken?: string;
}

export interface cosUrlInfoParams {
  /**
   * 用于分享外链压测时透传给后端
   * 预览模式：极速预览 official、缓存预览 cache、高清预览 high_definition、普通预览 ordinary
   */
  docPreviewMode?: string;
  contentDisposition?: 'inline' | 'attachment';
  purpose?: 'download' | 'preview';
  preview?: boolean;
  heightSize?: number;
  widthSize?: number;
  domainTag?: number;
}

export interface CheckFileParams {
  size?: number;
  spaceId: string;
  collectToken?: string;
}
export interface Headers {
  [key: string]: string;
}
export interface UploadPartInfo {
  domain: string;
  path: string;
  uploadId: string;
  headers: Headers;
  expiration: string;
  // 私有云无 headers 字段，必有 parts 字段。
  parts: Record<
    number,
    {
      [key: string]: unknown;
      headers: Headers;
    }
  >;
}
export interface UploadFileData extends UploadPartInfo {
  confirmKey: string;
}
export interface UploadMultiPartFileData extends UploadFileData {
  confirmKey: string;
  uploadId: string;
}
export interface UpdateUploadMultiPartInfo extends BaseParams {
  confirmKey: string;
  collectToken?: string;
}
export interface UpdateMultiPartFileInfoData extends UploadPartInfo {
  uploadId: string;
}
export interface ConfirmUploadFileParams extends BaseParams {
  confirmKey: string;
  conflictResolutionStrategy?: string;
  noUploadPartInfo?: number;
  crc64?: string;
  collectToken?: string;
}
export interface CancelFileUploadParams extends BaseParams {
  confirmKey: string;
}
export interface Parts {
  PartNumber: number;
  LastModified: string;
  ETag: string;
  Size: number;
}
export interface GetFileUploadStatusData {
  confirmed: boolean;
  path: string[];
  type: string;
  creationTime: string;
  force: boolean;
  parts: Parts[];
  uploadId: string;
  uploadPartInfo?: UploadPartInfo;
}
export interface ConfirmUploadFileData {
  path: string[] | null;
}
export interface FileCreateDocParams extends FileParams {
  fromTemplate: string;
}
export interface FileCreateStencilParams extends FileParams {
  templateIds: number[];
}
export interface DeleteFileParams extends FileParams {
  permanent?: number;
  syncId?: number;
}
export interface DeleteFileData {
  recycledItemId: string;
}
export interface RenameOrMoveFileParams extends FileParams {
  conflictResolutionStrategy?: string;
  from: string;
  syncId?: number; // TODO 等新接口好了需要删除
}
export interface CopyFileParams extends FileParams {
  conflictResolutionStrategy?: string;
  copyFrom: string;
}
export interface MoveRequestItem {
  from: string;
  to: string;
  moveAuthority?: boolean;
  type?: string;
  id?: number;
  conflictResolutionStrategy?: string;
}
export interface CopyRequest {
  copyFrom: string;
  to: string;
  id?: number;
  conflictResolutionStrategy?: string;
  type?: string;
}

export interface BatchDownloadParams {
  spaceId: string;
  shareToken?: string;
  sharePreviewerOrganizationId?: number;
}

export interface BatchDownloadData {
  taskId?: number;
  secret: string;
  sign: string;
}
/** 文件操作模块 end  */

/** 文件标签操作模块 start  */
export interface FileTag {
  tagId?: number;
  tagName?: string;
  tagValue?: string;
}
export interface CreateFileTagParams {
  tagName: string;
  tagType: 0 | 1 | number;
}

export type SetFileTagsBody = FileTag[];
/** 文件标签操作模块 end  */

/** 文件同步操作接口 start */
export type SyncId = number;
export type RelativePath = string;
export type SSN = string;
export interface SyncBaseParams {
  cancelToken?: CancelToken;
}
export interface GetJournalParams extends SyncBaseParams {
  syncId: SyncId;
  dirNode?: string; // 若不传，获取token时自动从atm中取
  ssnMarker?: string;
  journalMarker?: string;
  journalLimit?: number;
}
export interface JournalUpdateFolderItemData {
  type: 'dir';
  path: string[];
  inode: string;
  ssn: string;
  modificationTime: string;
}
export interface JournalUpdateFileItemData {
  type: 'file';
  path: string[];
  inode: string;
  ssn: string;
  crc64: string;
  modificationTime: string;
  size: string;
}
export interface JournalDeleteFolderItemData {
  type: 'deletion';
  parentPath: string[];
  parentInode: string;
  ssnDeletion: SSN;
  modificationTime: string;
}
export interface GetJournalData {
  nextSSNMarker: string;
  nextJournalMarker: string;
  contents: (JournalUpdateFolderItemData | JournalUpdateFileItemData | JournalDeleteFolderItemData)[];
}
// 文件同步操作基础传参
export interface FileSyncOperateBaseParams extends SyncBaseParams {
  dirNode?: string;
  syncId: SyncId;
  relativePath: RelativePath;
  ssn?: SSN;
}
export interface CheckPathAvailabelParams extends FileSyncOperateBaseParams, SyncBaseParams {
  ssn?: SSN;
}

export type GetPathInfoParams = CheckPathAvailabelParams;

export enum SyncPathType {
  FileType = 'file',
  DirType = 'dir',
}

export interface SyncFilePathInfoData {
  creationTime: string;
  modificationTime: string;
  crc64: string;
  inode: string;
  ssn: string;
  size: string;
  type: SyncPathType.FileType;
}

export interface SyncFilePathCosUrlData {
  creationTime: string;
  modificationTime: string;
  cosUrl: string;
  crc64: string;
  inode: string;
  ssn: string;
  availableCosUrls?: string[];
  size: string;
  type: SyncPathType.FileType;
}

export interface SyncDirPathInfoData {
  creationTime: string;
  modificationTime: string;
  isEmpty: boolean;
  inode: string;
  ssn: string;
  ssnDeletion: string;
  type: SyncPathType.DirType;
}
export type SyncPathInfoData = SyncFilePathInfoData | SyncDirPathInfoData;
export type SyncPathCosUrlData = SyncFilePathCosUrlData | SyncDirPathInfoData;
export interface ListPathParams extends FileSyncOperateBaseParams, SyncBaseParams {
  ssnDeletion: SSN;
}
export interface ListPathData {
  nextMarker?: string;
  contents: Array<{
    type: 'file' | 'dir';
    name: string;
  }>;
}

export interface SyncUploadFileParams extends FileSyncOperateBaseParams {
  fileSize?: number;
  fullHash?: string;
  beginningHash?: string;
  size?: string;
  partNumberRange?: string;
  ssn?: string;
  modificationTime?: string;
}

export interface SyncUploadPartInfo extends FileSyncOperateBaseParams {
  domain: string;
  path: RelativePath;
  uploadId: string;
  headers: Headers;
  expiration: string;
  // 私有云无 headers 字段，必有 parts 字段。
  parts: Record<
    number,
    {
      [key: string]: unknown;
      headers: Headers;
    }
  >;
}
export enum SyncOperateType {
  create = 'create',
  delete = 'delete',
  move = 'move',
  copy = 'copy',
}
export interface SyncCreateOperateParams {
  action: SyncOperateType.create;
  path: RelativePath;
}
export interface SyncDeleteOperateParams {
  action: SyncOperateType.delete;
  path: RelativePath;
  ssn: string;
}
export interface SyncMoveOperateParams {
  action: SyncOperateType.move;
  from: RelativePath;
  to: RelativePath;
  ssn: string;
  conflictResolutionStrategy: string;
}
export interface SyncCopyOperateParams {
  action: SyncOperateType.copy;
  from: RelativePath;
  to: RelativePath;
  ssn: string;
}
export type SyncBatchOperateBodyParams = Array<
  SyncCreateOperateParams | SyncDeleteOperateParams | SyncMoveOperateParams | SyncCopyOperateParams
>;
export interface SyncBatchOperateParams {
  syncId: SyncId;
}

enum SyncBatchOperateAction {
  Create = 'create',
  Delete = 'delete',
  Move = 'move',
  Copy = 'copy',
}
export interface SyncBatchOperateData {
  result: Array<{
    action: SyncBatchOperateAction;
    path?: string[];
    inode: string;
    status: number;
    ssn: string;
    modificationTime?: string;
    message?: string;
    code?: string;
    to?: string[];
    from?: string[];
    size?: string;
    crc64?: string;
  }>;
  taskId?: number;
}

export interface SyncUploadFileData extends SyncUploadPartInfo {
  confirmKey: string;
}
export interface SyncUploadMultiPartFileData extends SyncUploadFileData {
  confirmKey: string;
  uploadId: string;
}
export interface SyncUpdateUploadMultiPartInfo {
  syncId: SyncId;
  confirmKey: string;
  partNumberRange?: string;
}
export interface SyncUpdateMultiPartFileInfoData extends SyncUploadPartInfo {
  uploadId: string;
}
export interface SyncConfirmUploadFileParams {
  confirmKey: string;
  syncId: SyncId;
  conflictResolutionStrategy?: string;
  crc64?: string;
  noUploadPartInfo?: number;
}
export interface SyncCancelFileUploadParams {
  confirmKey: string;
  syncId: SyncId;
}
export interface SyncGetFileUploadStatusData {
  confirmed: boolean;
  path: string[];
  type: string;
  creationTime: string;
  force: boolean;
  parts: Parts[];
  uploadId: string;
  uploadPartInfo?: SyncUploadPartInfo;
}
export interface SyncConfirmUploadFileData {
  path: string[] | null;
  inode: string;
  ssn: string;
  size: string;
  modificationTime: string;
  crc64: string;
}

// TODO: 下面的同步接口类型都要修改
export enum SyncMode {
  LocalToCloud = 'local_to_cloud',
  CloudToLocal = 'cloud_to_local',
  TwoWay = 'two_way',
}
export interface CreateSyncParams {
  spaceTag: SpaceTag;
  cloudPath: string;
  localPath: string;
  clientId: string;
  mode: SyncMode;
}

export interface CreatePersonalSyncParams extends CreateSyncParams {
  spaceTag: SpaceTag.personal;
}
export interface CreateTeamSyncParams extends CreateSyncParams {
  spaceTag: SpaceTag.team;
  teamId: TeamId;
}
export interface CreateSyncData {
  syncId: SyncId;
}
export interface GetSyncInfoParams {
  syncId: SyncId;
}
export interface GetSyncInfoData extends CreateSyncParams {
  syncId: SyncId;
}
export interface QuerySyncInfoParams {
  id_list: string; // syncId1,syncId2,syncId3,syncId4
}
export interface QuerySyncInfoData {
  syncId: SyncId;
  status: string;
  cloudPath: string[];
  localPath: string;
  clientId: string;
  mode: SyncMode;
}

export interface migrateSyncInfoParams {
  list: number[];
}

export interface migrateSyncInfoData {
  syncId: SyncId;
  lsyncId: SyncId; // 老同步盘id
  status: string;
  cloudPath: string[];
  mode: string;
  creationTime: string;
  localPath: string;
  deviceId: string;
  spaceId: string;
  spaceTag: SpaceTag;
  orgId: number;
  teamId?: number;
  teamPath?: string[];
}

export interface DeleteSyncParams {
  syncId: SyncId;
}

export type DeleteSyncData = EmptyData;
/** 文件同步操作接口 end */

/** 历史版本接口 start  */
export interface HistoryItem {
  crc64: string;
  createdBy: number;
  createdByOrgId: string;
  createdUserAvatar: string;
  createdUserId: string;
  createdUserNickname: string;
  creationTime: string;
  creationWay: number; // 0：创建，1：更新；2：恢复
  id: number;
  isLatestVersion: boolean;
  name: string;
  size: string;
  version: number;
}
export interface GetHistoryListParams extends GetFavoriteListParams {
  dirPath: string;
  spaceOrgId: number;
}
export interface GetHistoryListData {
  totalNum: number;
  contents: HistoryItem[];
}
export interface SetLatestVersionParams extends SpaceBaseInfo {
  historyId: number;
}
export interface HistoryExtensionParams {
  enableFileHistory: boolean;
  fileHistoryCount?: number;
  fileHistoryExpireDay?: number;
  isClearFileHistory?: boolean;
}
export interface UpdateLibraryHistoryExtensionParams {
  historyExtensionParams: HistoryExtensionParams;
}
export type GetLibraryHistoryExtension = HistoryExtensionParams;
/** 历史版本接口 end  */

/** 安全扫描接口 start */
export interface SecurityScanParams {
  enableAntivirus: boolean;
}
export type GetSecurityScanParams = SecurityScanParams;
/** 安全扫描接口 end */

/** oauth2接口 start */
export interface AuthItem {
  libraryId: string;
  spaceTag: SpaceTag;
  withAllSpace?: boolean;
  path?: Array<string>;
}
export interface GetOauthCodeParams {
  organizationId: OrganizationId;
  /** 应用 ID，必填 */
  appId: string;
  /** 回调地址，必填； */
  redirectUri: string;
  /** 授权级别：空间级 or 文件级 */
  directoryScopes: Array<AuthItem>;
  /** 授权接口范围，可选，默认 ALL，如有多个需逗号隔开； */
  scope?: string;
  /** 重定向后会带上 state 参数，开发者可以填写a-zA-Z0-9的参数值，最多128字节； */
  state?: string;
  /** 是否自动跳转，1 或 0，可选参数，默认 0； */
  autoRedirect?: number;
}
export interface OauthData {
  redirectUriWithCode: string;
}

export interface GetOauthInfoParams {
  appId: string;
}
export interface OauthInfoData {
  appName: string;
  userMustHaveAuthority: {};
  interfaceScopes: Array<string>;
}

/** oauth2接口 end */

/** 文件收集接口 start */
export interface CollectTaskParams {
  collectId?: number;
  collectToken?: string;
  countryCode?: string;
  phoneNumber?: string;
  type?: string;
}
export interface CollectTaskData {
  id: number;
  nickname: string;
  userId: number;
  avatar: string;
  title: string;
  describes: string;
  namingRules: string[];
  fileType: string[];
  code: string;
  expireTime: string;
  isPermanent: boolean;
  status: number;
  exampleFiles: Array<SpaceFileItemData>;
  libraryId: string;
  spaceId: string;
  path: string;
  spaceOrgId: string;
  orgId: number;
  orgLogo: string;
  orgName: string;
  showOrgNameInUI: boolean | undefined;
}

export interface CollectHistoryItem {
  contentType: string;
  name: string;
  size: number;
  creationTime: string;
}
export interface CollectAccessTokenData {
  collectAccessToken: string;
  expiresIn?: number;
}

export interface CollectSubmit {
  userId: number;
  orgId: number;
  avatar: string;
  nickname: string;
  submitTime: string; // 最近一次上传
  fileNum: number; // 上传总份数
}
export interface CollectStatusData {
  submit: CollectSubmit[];
  submitUserCount: number;
  allowedUserCount: number; // 企业
}
/** 文件收集接口 end */

/** 文件审批接口 start */
export interface FileApplyParams {
  spaceId: string;
  pathList: string[];
  title: string;
  reason: string;
  roleId: number;
}

export interface FileApplyItem {
  spaceId: string;
  path: string;
}

export interface FileApplyData {
  successItems: FileApplyItem[];
  failedItems: FileApplyItem[];
  applyNo: string;
}

export interface FileApplyNoParams {
  applyNo: string;
}

export interface FileApplyPageParams {
  type: 'my_audit' | 'my_apply';
  status: '' | 'auditing' | 'history';
  marker?: string;
  limit?: number;
  page?: number;
  pageSize?: number;
  orderBy?: 'creationTime' | 'operationTime' | string;
  orderByType?: 'asc' | 'desc' | string;
}

export interface FileApplyPageItem {
  applyNo: string;
  title: string;
  canAuditUsers: UserInfoAvatarData[];
  directoryList: DirectoryItem[];
  auditStatus: 0 | 1 | 2 | 3 | 4; // 0 审批中 1 已失效 2 已撤回 3 已驳回 4 审批通过
  creationTime: string;
  createdByAvatar: string;
  createdByNickname: string;
  operationTime: string;
  roleId: number;
}

export interface FileApplyPageData {
  loading?: boolean;
  hasMore?: boolean;
  totalNum: number;
  nextMarker?: string;
  contents: FileApplyPageItem[];
}

export interface DirectoryItem {
  isValid: boolean;
  name: string;
  type: string;
  fileType: string;
  oldRoleIds: number[];
  path: string[];
}

export interface TeamItem {
  id: number;
  orgId: number;
  name: string;
  parentId: number;
  spaceId: string;
  defaultRoleId: string;
}

export interface FileLocationInfo {
  // 空间标签 个人 | 企业空间 | 协作群组
  spaceTag: SpaceTag | '';
  group?: {
    id: number; // 群组id
    name: string; // 群组名称
    orgId: number; // 群主对应的orgId
    spaceId: string; // 群主开启群，使用的空间ID
    userId: number; // 群主的userid
  };
  team?: {
    id: number; // 企业对应的Id
    name: string; // 团队名称
    orgId: number; // 企业对应的组织机构id
    parentId: number; // 对应的企业的id
    spaceId: string; // 企业空间对应的空间ID
    path?: Array<string>; // 企业空间对应的空间路径
  };
  user?: {
    id: number; // 用户id
    name: string; // 用户昵称
    orgId: number; // 用户所属的组织机构
    spaceId: string; // 用户个人空间对应的spaceId
    userId: number; // 用户id 有点重复了不过不碍事
  };
}

export interface FileApplyDetailData extends FileLocationInfo {
  applyNo: string;
  title: string;
  reason: string;
  canAuditUsers: UserInfoAvatarData[];
  auditStatus: 0 | 1 | 2 | 3 | 4; // 0 审批中 1 已失效 2 已撤回 3 已驳回 4 审批通过
  auditStatusCause: 0 | 1 | 2 | 3 | 4 | 5; // 0 正常 1 文件删除 2 空间删除 3 用户删除 4 角色删除 5 用户已被移出群组
  creationTime: string;
  createdBy: number;
  createdByCountryCode: string;
  createdByPhoneNumber: string;
  createdByAvatar: string;
  createdByNickname: string;
  auditByAvatar: string;
  auditByNickname: string;
  operationTime: string;
  roleId: number;
  directoryList: DirectoryItem[];
  isAdmin: boolean;
  directoryPath: string[];
  createByBelongLatestTeam: TeamItem;
  isCreatedByExist: boolean;
  isUserBelongSpace?: boolean;
  spaceId?: string;
  relatedToMePath?: string[] | null;
}

export interface FileApplyCheckParams {
  spaceId: string;
  pathList: string[];
}

export interface FileApplyCheckData extends FileLocationInfo {
  spaceId: string;
  path: string;
  // 是否已处于权限审批流程
  hasApplied: boolean;
  // 文件审批列表（应该只有一个值，因为已经发起审批的文件不能发起第二次）
  applyNoList: string[];
  // 审批之前的角色id
  oldRoleIds: number[];
  isValid: boolean;
}
/** 文件审批接口 end */

/** 防勒索 start  */
export interface RansomwareWarnParams {
  equipment: string;
}
/** 防勒索 end  */

/** 回收站接口 start  */
export interface SpaceItem {
  spaceId: SpaceId;
  includeChildSpace: boolean;
}
export interface ListRecycledParams extends BaseParams {
  marker?: number; // 用于顺序列出分页的标识，可选参数，不能与 page 和 page_size 参数同时使用；
  limit?: number; // 用于顺序列出分页时本地列出的项目数限制，可选参数，不能与 page 和 page_size 参数同时使用；
  page?: number; // 分页码，默认第一页，可选参数，不能与 marker 和 limit 参数同时使用；
  pageSize?: number; // 分页大小，默认 20，可选参数，不能与 marker 和 limit 参数同时使用；
  orderBy?: 'name' | 'modificationTime' | 'size' | 'removalTime' | 'remainingTime' | string;
  orderByType?: string;
  removedBy?: string;
  spaceItems: SpaceItem[];
  withAllTeamSpace?: boolean;
  withAllGroupSpace?: boolean;
}

export interface RecycledItemBaseInfo {
  name: string;
  type: string;
  removalPerson: string;
  size: string;
  removalTime: string;
  remainingTime: string;
  path: string[];
  spaceTag: SpaceTag;
  team?: TeamInfoBaseData;
  user?: UserInfoBaseData;
  group?: GroupInfoBaseData;
}
export interface RecycledListItemInfo {
  recycledItemId: number;
  spaceId: string;
  originalPath: Array<string>;
  size: string;
  previewByDoc: boolean;
  previewByCI: boolean;
  previewAsIcon: boolean;
  fileType: string;
  name: string;
  type: string;
  creationTime: string;
  modificationTime: string;
  removalTime: string;
  remainingTime: string;
  authorityList: {
    canRestore: boolean;
    canDelete: boolean;
  };
  removedByNickname: string;
  team?: TeamInfoBaseData;
  user?: UserInfoBaseData;
  group?: GroupInfoBaseData;
}

export interface ListRecycledData {
  nextMarker: number;
  totalNum: number;
  contents: RecycledListItemInfo[];
}

export type RecycledItemId = number;

export type RecycledItem = {
  spaceId: string;
  recycledItemId: RecycledItemId;
};

export interface RecycledParams extends BaseParams {
  recycledItems: Array<RecycledItem>;
  withAllGroups?: boolean;
}

export interface GetRecycledItemInfoParams {
  recycledItemId: RecycledItemId;
  spaceId: SpaceId;
  spaceOrgId: number;
}

export interface IPRestrictionItem {
  id: number;
  orgId?: number;
  rule: {
    startIp: string;
    endIp?: string;
    ipType: number;
  };
  enabled: boolean;
}

export interface EditIPRestrictionParams {
  id: number;
  startIp?: string;
  endIp?: string;
  enabled?: boolean;
}

export type IPRuleItem = {
  startIp: string;
  endIp?: string;
};

export interface BatchMoveItem {
  from: string;
  to: string;
  conflictResolutionStrategy?: string;
  moveAuthority?: boolean;
}
/** 回收站接口 end  */

/** 文件共享接口 start  */
export interface ListShareParams extends BaseParams {
  page?: number;
  pageSize?: number;
  orderBy?: string;
  orderByType?: string;
  shareToke?: string;
}
export interface ListShareData {
  totalNum: number;
  contents: SpaceFileItemData[];
}
/** 文件共享接口 end  */

/** 共享授权接口 start  */
export interface AuthorityDirectoryParams extends BaseParams {
  dirPath: string;
  version?: string; // 2.2版本升级后，因为数据结构变化，需要暂时用该字段调用接口
  spaceOrgId?: number;
}
export type DirAuthorize = {
  roleId: number; // 授权的角色：操作者 or 上传者等
  userId?: number; // 授权用户的 id
  spaceId?: SpaceId; // 授权团队的空间 id
  name?: string; // 授权的昵称或团队名称
  teamRoleId?: number;
};
export interface PutAuthorityDirectoryParams extends AuthorityDirectoryParams {
  authorizeTo: DirAuthorize[];
}
export interface GetAuthorityDirectoryParams extends AuthorityDirectoryParams {
  limit: number;
  marker?: string;
  keyword?: string;
}

export type DeleteAuthorityDirectoryParams = PutAuthorityDirectoryParams;
export interface GetAuthorityDirectoryData {
  name: string;
  tag: number;
  roleId: number;
  userId?: string;
  spaceId?: SpaceId;
  user?: UserBaseInfo;
  team?: TeamTreeNode;
  isForbidden?: boolean;
  isDefault?: boolean;
}
/** 共享授权接口 end  */

/** 创建搜索任务接口 start  */
export interface SearchTaskData<T> {
  searchId: string; // 搜索任务 ID，用于后续轮询和加载更多
  hasMore: boolean; // 是否有更多，当值为true时可以继续获取更多搜索结果
  nextMarker: number;
  contents: T[];
  // 下面是搜索日志的返回，后期需要优化此处（搜索团队也是使用这个 type，需要分开）
  searchFinished?: boolean;
  lastHit: T;
  hitsCount: number;
  currentAvailable: number;
  allResultCovered: boolean;
}
export interface SearchTeamParams {
  keyword?: string | number;
  page?: number;
  pageSize?: number;
}
export interface CreateSearchTeamTaskParams {
  checkChildren?: boolean;
  keyword: string;
  ancestorId?: number; // 搜索范围，指定搜索的祖先团队，如搜索所有团队可不指定该字段
  checkManagementPermission?: 0 | 1;
  checkBelongingTeams?: 0 | 1;
  withNoSpaceTeams?: 0 | 1;
}
export interface QueryTeamMemberParams extends SearchTeamParams {
  teamId: string | number;
  withBelongingTeams?: boolean;
  withSpaceUsage?: boolean;
  showPhoneNumber?: boolean;
  orderBy?: string;
  orderByType?: string;
  includeInactiveUser?: number;
}
// 文件搜索类型
export type SearchFileType =
  | 'all'
  | 'dir'
  | 'file'
  | 'doc'
  | 'ppt'
  | 'xls'
  | 'pdf'
  | 'txt'
  | 'image'
  | 'video'
  | 'audio'
  | 'other';
export interface CreateSearchTaskParams extends BaseParams {
  keyword: string;
  scope?: string;
  type?: SearchFileType[];
  modificationTimeStart?: string;
  modificationTimeEnd?: string;
  minFileSize?: number;
  maxFileSize?: number;
  creators?: [];
  searchBy?: string; // 根据文件名或文件内容搜索
  accurate?: boolean; // 精准搜索 or 模糊搜索
  marker?: number;
  limit?: number;
  orderBy?: string; // 排序仅在精准搜索时用
  orderByType?: string;
  spaceOrgId?: number; // 外部成员搜索群组内容需要
}

interface SearchTeamInfoBaseData {
  id: number;
  name: string;
  spaceId: SpaceId;
  parentId: number;
  orgId: number;
  path: string;
}

interface SearchUserInfoBaseData {
  id: string;
  name: string;
  orgId: string;
  spaceId: string;
}

export interface SearchFileData extends SpaceFileItemData {
  modifierName: string;
  spaceTag?: SpaceTag;
  relatedToMePath?: string[] | null;
  team?: SearchTeamInfoBaseData;
  group?: GroupInfoBaseData;
  user?: SearchUserInfoBaseData;
  isUserBelongSpace?: boolean;
  highlight?: {
    name?: string[];
    fileContent?: string[];
  };
}

/** 创建搜索任务接口 end  */

/** 异步任务模块 start  */
export interface AsyncTaskParams extends BaseParams {
  taskIdList: number | string;
}
export interface TaskResultItem {
  taskId: number;
  status: number;
  remaining: number;
  total: number;
  result?: [];
}
export type TaskResult = TaskResultItem[];
/** 异步任务模块 end  */

/** 登录模块 start  */
export type ChannelTag = 'meeting' | 'hiflow' | string;

export interface SendSMSData {
  countryCode: string;
  phoneNumber: string;
  flag?: string | number;
  domain: string;
  type?: 'sign-in' | 'bind-meeting-phone';
  captchaTicket?: string;
  captchaRandstr?: string;
  collectToken?: string;
  inviteCode?: string;
  pf?: ChannelTag;
}
// 获取短信验证码
export interface SMS {
  // 手机号国家代码
  countryCode: string;
  // 手机号
  phoneNumber: string;
}
export interface LoginParams {
  countryCode: string;
  phoneNumber: string;
  flag?: string | number;
  // 短信验证码
  code: string;
  // 设备名 如iphone 12
  deviceId?: string;
  domain: string;
  // 图形验证码信息
  captchaTicket: string;
  captchaRandstr: string;
  inviteCode?: string;
  collectToken?: string;
  pf?: ChannelTag;
}
export interface LoginData {
  userId: UserId;
  userToken: UserToken;
  expiresIn: number;
  organizations: OrganizationItem[];
}
export interface RegisterParams extends LoginParams {
  orgName: string;
  business: string;
  role?: string;
  scale?: string;
  wechatCode?: string;
}
export type RegisterData = LoginData;
export interface GetQRCodeData {
  uuid: string;
}
export interface GetQRCodeStatusData extends GetQRCodeData {
  status: string;
}
export interface GetQRCodeLoginData extends GetQRCodeData {
  userId: number;
  userToken: string;
  expiresIn: number;
  organizations: OrganizationItem[];
}
/** 登录模块 end  */

/** 批量操作接口 start  */
export interface BatchMoveParams extends BaseParams {
  batchList: Array<BatchMoveItem>;
}
export interface BatchDeleteItem {
  path: string;
  permanent?: boolean;
  id?: number;
}

export interface BatchDeleteParams extends BaseParams {
  batchList: Array<BatchDeleteItem>;
}

/** 批量操作接口 end  */

/** 米大师接入模块 start  */
export interface MidasGetPurchaseIdData {
  completed: boolean;
  purchaseId: string;
  paid: boolean;
  purchaseOrgId: 0 | 1 | number;
}
/** 米大师接入模块 end  */

/** 腾讯会议模块 start  */
export interface CheckMeetingAuthData {
  available: boolean;
}

export interface joinMeetingData {
  joinUrl?: string;
  error_code?: number;
  new_error_code?: number;
  message?: string;
  status?: string;
}
export interface meetingRecordConfig {
  teamId: number;
  path: string;
}
export interface recurringRule {
  recurring_type: number;
  until_type: number;
  until_date?: number;
  until_count: number;
}
export interface meetingConfig {
  subject: string;
  type: number;
  start_time: string;
  end_time: string;
  meeting_type?: string;
  password?: string;
  recurring_rule?: recurringRule;
  enable_doc_upload_permission?: boolean;
}
export interface createMeetingParams extends BaseParams {
  meetingRecordConfig?: meetingRecordConfig;
  meetingConfig: meetingConfig;
}
export interface meetingListInfo {
  subject: string;
  meeting_id: string;
  meeting_code: string;
  status: string;
  start_time: string;
  end_time: string;
  hosts: string[];
  join_meeting_role: string;
  meeting_type: number;
  recurring_rule: recurringRule;
  has_more_sub_meetings: number;
  remain_sub_meetings: number;
  current_sub_meeting_id: string;
}
export interface meetingListsData {
  meeting_number: number;
  next_pos: number;
  remaining: number;
  meeting_info_list: Array<any>;
}

export interface MeetingSign {
  sdkId: string;
  corpId: string;
  signature: string;
  nonceStr: string;
  timestamp: string;
}

/** 腾讯会议模块 end  */

export interface GetInvitationInfoData {
  expireTime: string;
  expired: boolean;
  hasJoined: boolean;
  allowExternalUser: boolean;
  userLimitReached: boolean;
  orgId: number;
  type: 'group' | 'org' | 'temporary';
  organization: {
    name: string;
    logo: string;
    showOrgNameInUI: boolean | undefined;
  };
  group: {
    name: string;
    id: number;
  };
  invitor: {
    nickname: string;
    avatar: string;
  };
  editionFlag?: string;
  currentOrgUserCount?: number;
  orgUserCountLimit?: number;
  domains: DomainItem[];
}

export interface JoinGroupOrOrgData {
  isNew: boolean;
  isSuccess: boolean;
}

/**
 * 注销相关接口
 */
export interface CheckDeregisterData {
  canDeregister: boolean;
  hasGroup: boolean;
  hasPersonalSpaceFile: boolean;
}
/**
 * 注销相关接口 end
 */

/** 自定义域名相关接口类型 Start */
// 绑定域名信息
export interface CustomDomainParams {
  domain: string;
  key?: string;
  cert?: string;
  icp?: string;
  psb?: string;
  title?: string;
}

export interface CustomDomainData {
  domain: string;
  isCustom?: boolean;
  status: number;
  secretName?: string;
  cipChecked?: boolean; // 域名是否已备案
  icp?: string; // ICP备案号
  psb?: string; // 公安备案号
  title?: string; // 网页名称
  certInfo?: {
    certDomain: string;
    certAltDomains: string[];
    certIssuedAt: string;
    certExpiresAt: string;
    expired: boolean;
  };
}
/** 自定义域名相关接口类型 end  */

/**
 *  安全相关接口
 */
export interface CloudHerbsParams {
  limit: number;
  orderBy?: string;
  orderByType?: string;
  spaceId?: string;
  createdBy?: string;
  filterType?: string;
  spaceItems: SpaceItem[];
}

export interface SensitiveDataParams {
  id: number;
  orgId: number;
  word: string;
  hitCount?: number;
  trustCount?: number;
  userId: number;
  scanProgress?: number;
  creationTime: string;
}
export interface CreateSensitiveData {
  failedItems: {
    word: string;
  }[];
  successItems: {
    word: string;
  }[];
}

export interface ContentData {
  name: string;
  type: string;
  userId: string;
  fileType: string;
  size: string;
  path: string[];
  creationTime: string;
  virusName: string;
  spaceId: string;
  canMarkAsNonVirus: boolean;
  canMarkAsNonSensitiveWord: boolean;
  suspiciousType: 0 | 1; // 0表示病毒，1标识敏感文件
  spaceTag: SpaceTag;
  spaceOrgId: number;
  group?: any;
  team?: any;
  user?: any;
  originPath: string;
  sensitiveWordName: string;
  inDirectory: boolean;
}

export interface SensitiveWordData {
  authorityList: CustomRole;
  creationPerson: string;
  creationTime: string;
  fileType: string;
  group: any;
  team: any;
  modificationTime: string;
  name: string;
  path: Array<string>;
  previewAsIcon: boolean;
  previewByCI: boolean;
  previewByDoc: boolean;
  sensitiveWordAuditStatus: number;
  spaceId: string;
  spaceTag: SpaceTag;
  type: string;
  userId: string;
  virusAuditStatus: number;
  originPath?: string;
  inDirectory: boolean;
  inDirectoryGroup: boolean;
  versionId: null | number;
  isUserBelongSpace?: boolean; // 是否属于本空间
  inRecycledDirectory: boolean;
}
export interface CloudHerbsData {
  nextMarker: string;
  contents: ContentData[];
}

export interface SensitiveWordListData {
  totalNum: string;
  contents: SensitiveWordData[];
}
export interface TrustFileItem {
  spaceId: string;
  path: string[];
}
export interface TrustFileData {
  restoreItems?: TrustFileItem[];
  directoryList?: TrustFileItem[];
}
/** 云查毒相关 end */

/** 水印canvas相关配置 start */
// 视频水印配置接口
export interface PreviewWatermarkConfig {
  font: string;
  textColor: string;
  textDeg: number;
  textGridX: number;
  textGridY: number;
  textNumX: number;
  textNumY: number;
  x: number;
  y: number;
  zIndex: string;
}
/** 水印canvas相关配置 end */

/**
 * 文件收集相关接口定义
 */

export interface launchedFileCollectionData {
  title: string;
  describes: string;
  path: string;
  expireTime?: string;
  allowedUsers?: any[];
  exampleFiles: any[];
  namingRules: string[];
  fileType: string[];
  spaceId?: string;
  spaceOrgId?: string;
  isPermanent?: boolean;
  code?: string;
}
export interface launchedFileCollectionParams extends launchedFileCollectionData {
  collectId?: number;
}

export interface FileCollectionListData {
  collectId: number;
  title: string;
  creationTime?: string;
  expireTime: string;
  nickname: string;
  userId: number;
  avatar: string;
  orgId: number;
  status: number;
  allowedUserCount?: number;
  submitUserCount?: number;
  spaceId?: string;
  spaceOrgId: number;
  path: string;
  isPermanent?: boolean;
  allowedUsers: any[];
  namingRules?: string[];
  exampleFiles?: any[];
  fileType?: string[];
}

export interface fileCollectInfoData extends FileCollectionListData {
  code?: string;
  domains: DomainItem[];
}

interface FileCollectSubmitData {
  userId: number;
  orgId: number;
  avatar: string;
  nickname: string;
  submitTime: string;
  fileNum: number;
  finish: number;
}
export interface FileSubmitStatusData {
  submit: FileCollectSubmitData[];
  submitUserCount: number | string;
  allowUserCount: number | string;
}

export interface getCollectionListData {
  totalNum: number;
  contents: FileCollectionListData[];
}

/**
 * 自定义问卷调查
 */
export interface CreateOrgInfo {
  role?: string;
  scale?: string;
  orgName?: string;
  business: string;
}

/**
 * 自定义域名相关接口类型 End
 */

/**
 * 账密模块
 */
export interface UpdatePasswordParams {
  originalAccountPassword: string;
  accountPassword: string;
}

export interface UpdateAccountNameParams {
  originalAccountName: string;
  newAccountName: string;
}

export interface CreateResetTokenParams {
  userId: number;
}

export interface GetResetTokenParams {
  resetUserId: number;
}

export interface ResetToken {
  createdBy: number;
  creationTime: string;
  expireTime: string;
  id: number;
  isUpdated: boolean;
  orgId: number;
  token: string;
  userId: number;
  userLoginConfigId: string;
  isModified: boolean;
  modificationTime: string;
}

export interface GetResetTokenData {
  resetToken: null | ResetToken;
}

export interface GetResetTokenDetailParams {
  resetPasswordToken: string;
}

export interface GetResetTokenDetailData {
  nickname: string;
  avatar: string;
  accountName: string;
  expireTime: string;
}

export interface ResetNewPasswordParams {
  accountName: string;
  accountPassword: string;
  resetToken: string;
}

export interface StencilTypeData {
  templateTypeCN: string;
  templateType: string;
}

export interface StencilListParams {
  spaceId: string;
  spaceOrgId: number;
  flag: number;
  templateType?: string;
  marker?: number;
  limit?: number;
  fileType?: string;
  templateName?: string;
}
export interface StencilListContent {
  assetPath: string;
  templateName: string;
  templateType: string;
  preViewUrl: string;
  fileType: string;
  id: number;
}
export interface StencilListData {
  contents: StencilListContent[];
  hasMore: boolean;
  marker: number;
  totalNum: number;
}

/* ************************** */
/* 请求集合及其请求函数详细类型定义 */
/* ************************** */
/**

/**
 * @description 路径为 user 开头的 API 接口集合
 */
export interface UserRequestSet extends APISet {
  /**
   * @description 发起创建收集
   */
  launchedFileCollection: APIFunctionType<launchedFileCollectionParams, { code: string; domains: DomainItem[] }>;
  /**
   * @description 我发起的文件收集设置查看
   */
  getLaunchedFileCollectionSetting: APIFunctionType<{ collectId: number }, fileCollectInfoData>;
  /**
   * @description 我发起的收集设置修改
   */
  updateLaunchedFileCollectionSetting: APIFunctionType<launchedFileCollectionParams, { code: string }>;
  /**
   * @description 我发起的文件收集列表
   */
  getLaunchedFileCollectionList: APIFunctionType<ListTableParams, getCollectionListData>;
  /**
   * @description 我收到的文件收集列表
   */
  getReceivedFileCollectionList: APIFunctionType<ListTableParams, getCollectionListData>;
  /**
   * @description 文件提交状态查看
   */
  getFileSubmitStatus: APIFunctionType<{ collectId: number }, FileSubmitStatusData>;
  /**
   * @description 停止收集
   */
  stopFileCollection: APIFunctionType<{ collectId: number }, EmptyData>;
  /**
   * @description 重新开始文件收集
   */
  restartFileCollection: APIFunctionType<{ collectId: number }, EmptyData>;
  /**
   * @description 删除文件收集任务
   */
  deleteFileCollection: APIFunctionType<{ collectId: number }, EmptyData>;

  /**
   * @description 获取自定义域名信息
   */
  getOrgCustomDomain: APIFunctionType<{ organizationId: OrganizationId }, CustomDomainData[]>;
  /**
   * @description 创建自定义域名
   */
  createOrgCustomDomain: APIFunctionType<CustomDomainParams, { id: number }>;
  /**
   * @description 更新自定义域名
   */
  updateOrgCustomDomain: APIFunctionType<CustomDomainParams, EmptyData>;
  /**
   * @description 解绑自定义域名
   */
  unbindOrgCustomDomain: APIFunctionType<EmptyData, EmptyData>;
  /**
   * @description 列出当前登录用户所属组织
   */
  getUserOrg: APIFunctionType<{ userToken?: string }, GetUserOrgData>;
  /**
   * @description 登录进指定组织
   */
  loginOrg: APIFunctionType<{ organizationId: OrganizationId }, EmptyData>;
  /**
   * @description 查询组织信息
   */
  getOrgInfo: APIFunctionType<{ organizationId: OrganizationId }, GetOrgInfoData>;
  /**
   * @description 查询组织空间使用量
   */
  getOrgSpace: APIFunctionType<null, OrgSpaceData>;
  /**
   * @description 获取空间信息和访问令牌
   */
  getSpaceInfo: APIFunctionType<null, GetSpaceInfoData>;
  /**
   * @description 列出空间
   */
  hasPersonalSpace: APIFunctionType<null | { organizationId: number }, HasPersonalSpaceData>;
  /**
   * @description 列出团队空间
   */
  listTeamSpace: APIFunctionType<{ teamId: TeamId }, QueryTeamSpace>;
  /**
   * @description 设置团队空间容量（仅管理员）
   */
  setTeamSpace: APIFunctionType<{ teamId: TeamId; capacity: string | null; openSpace?: boolean }, EmptyData>;
  /**
   * @description 列出团队空间
   */
  getTeamSpace: APIFunctionType<{ teamId: number }, TeamSpace>;
  /**
   * @description 列出团队空间对应的动态
   */
  getDynamics: APIFunctionType<GetSpaceDynamicParams, GetSpaceDynamicData>;
  /**
   * @description 列出团队空间对应的动态
   */
  getDynamicsMore: APIFunctionType<GetSpaceDynamicMoreParams, GetSpaceDynamicData>;
  /**
   * @description 列出团队空间对应的动态
   */
  getWorkbenchDynamic: APIFunctionType<GetWorkbenchDynamicParams, GetSpaceDynamicData>;
  /**
   * @description 获取指定空间的 accessToken
   */
  getSpaceAccessToken: APIFunctionType<{ spaceId: SpaceId }, GetSpaceInfoData>;
  /**
   * @description 获取企业Logo简单上传文件参数
   */
  putOrgLogo: APIFunctionType<{ ext: string }, PutOrgLogoData>;
  /**
   * @description 确认上传企业Logo
   */
  postOrgLogo: APIFunctionType<{ filePath: string }, PostOrgLogoData>;
  /**
   * @description 更新企业其他信息
   */
  updateOrgInfo: APIFunctionType<
    {
      name?: string;
      extensionData?: any;
    },
    EmptyData
  >;
  /**
   * @description 创建问卷调查
   */
  createSurvey: APIFunctionType<{ type: string; data: CreateOrgInfo; addFlag?: boolean }, EmptyData>;
  /**
   * @description 获取调查结果
   */
  getSurvey: APIFunctionType<{ type: string }, Array<{ type: string; data: CreateOrgInfo }>>;
  /**
   * @description 删除重置调查结果
   */
  deleteSurvey: APIFunctionType<{ type: string }, Array<{ type: string; data: object }>>;
  /**
   * @description 查询组织分享列表
   */
  orgShareList: APIFunctionType<OrgShareListParams, GetSharedLinkListData>;
  /**
   * @description 查询协作群组分享列表
   */
  getGroupShareList: APIFunctionType<OrgShareListParams, GetSharedLinkListData>;
  /**
   * @description 获取分享链接
   */
  getShareLink: APIFunctionType<GetShareLinkParams, GetShareLinkData>;
  /**
   * @description 修改分享链接
   */
  updateSharedLink: APIFunctionType<UpdateSharedLinkParams, GetShareLinkData>;
  /**
   * @description 删除分享链接
   */
  deleteSharedLinks: APIFunctionType<null, EmptyData, null, number[]>;
  /**
   * @description 获取链接项详情（已分享成功）
   */
  getSharedLinkItem: APIFunctionType<{ shareId: number }, GetSharedLinkItemData>;
  /**
   * @description 获取外链列表
   */
  getSharedLinkList: APIFunctionType<ListTableParams, GetSharedLinkListData>;
  /**
   * @description 获取外链操作记录列表
   */
  getSharedLinkLogList: APIFunctionType<GetSharedLinkLogListParams, GetSharedLinkLogListData>;
  /**
   * @description 分享单个文件并获取对应的预览/编辑页
   */
  getSingleFileShareLink: APIFunctionType<GetSingleFileShareLinkParams, GetSingleFileShareLinkData>;
  /**
   * @description 获取意欲保存至网盘的选中文件的 code
   */
  saveShareCode: APIFunctionType<SaveShareCodeParams, SaveShareCodeData>;
  /**
   * @description 验证提取码
   */
  verifyExtractionCode: APIFunctionType<VerifyExtractionCodeParams, VerifyExtractionCodeData>;
  /**
   * @description 获取code存储信息
   */
  getCodeInfo: APIFunctionType<SaveShareCodeData, RequestData>;
  /**
   * @description 查询code访问次数
   */
  checkCodeVisit: APIFunctionType<SaveShareCodeData, CheckCodeVisitData>;
  /**
   * @description 添加code存储信息
   */
  saveCodeInfo: APIFunctionType<RequestParams, SaveShareCodeData>;
  /**
   * @description 获取分享文件详细信息
   */
  getSharedLinkDetail: APIFunctionType<{ shareToken: string }, GetSharedLinkDetailData>;

  /**
   * @description 获取组织企业微信配置参数
   */
  getWeworkConfig: APIFunctionType<null, LoginConfigData>;
  /**
   * @description 更新组织企业微信配置参数
   */
  updateWeworkConfig: APIFunctionType<UpdateWeworkConfigParams, RequestData>;
  /**
   * @description 获取代开发应用授权二维码
   */
  getWeworkDelegateQrcode: APIFunctionType<{}, GetWeworkDelegateQrcodeData>;
  /**
   * @description 我已配置
   */
  weworkAuthorized: APIFunctionType<{}, EmptyData>;
  /**
   * @description 我已配置
   */
  weworkOnline: APIFunctionType<{}, EmptyData>;
  /**
   * @description 获取代开发应用企业信息
   */
  getDelegateInfo: APIFunctionType<{}, GetDelegateInfoData>;
  /**
   * @description 清除企微配置
   */
  removeWeworkConfig: APIFunctionType<{}, EmptyData>;
  /**
   * @description 验证组织企业微信配置参数
   */
  checkWeworkConfig: APIFunctionType<WeworkConfigParams, RequestData>;
  /**
   * @description 立即同步企微架构（后台异步任务）
   */
  triggerSyncOrg: APIFunctionType<{ action: string; data?: { result: { syncWay: string } } }, { taskId: number }>;
  /**
   * @description 查询企微架构同步任务列表
   */
  getSyncWeworkOrgList: APIFunctionType<
    { action: SyncType },
    ListTableData<Task<SyncWeworkOrgResultData | AsyncTaskResult>>
  >;
  /**
   * @description 查看玉符后台配置
   */
  getIDaasConfig: APIFunctionType<null, IDaasConfigParams>;
  /**
   * @description 仅测试使用，用于解绑企微和idaas的绑定
   */
  removeAccountAssociation: APIFunctionType<{ type: 'yufu' | 'wework' }, RequestData>;
  /**
   * @description 修改玉符后台配置
   */
  updateIDaasConfig: APIFunctionType<UpdateIDaasConfigParams, RequestData>;
  /**
   * @description 验证玉符同步配置参数
   */
  checkIDaasConfig: APIFunctionType<IDaasConfigParams, RequestData>;
  /**
   * @description 清除yufu配置
   */
  removeYufuConfig: APIFunctionType<{ type: 'sso' | 'sync' | string }, EmptyData>;
  /**
   * @description 设置统一身份认证协议配置
   */
  setAccountProtocol: APIFunctionType<AccountProtocolParams, EmptyData>;
  /**
   * @description 获取统一身份认证配置协议
   */
  getAccountProtocol: APIFunctionType<ProtocolParams, AccountParams>;
  /**
   * @description 获取支持协议列表
   */
  getSupportProtocolList: APIFunctionType<EmptyData, string[]>;
  /**
   * @description 删除统一身份认证配置协议
   */
  removeAccountProtocol: APIFunctionType<ProtocolParams, EmptyData>;
  /**
   * @description 设置统一身份认证协议配置
   */
  testAccountProtocol: APIFunctionType<AccountProtocolParams, AccountTestResultData | EmptyData>;

  /**
   * @description 统计系统用户已买账号数、已使用数
   */
  getUserCount: APIFunctionType<null, UserCountData>;
  /**
   * @description 统计用户用量规格分布
   */
  getSpaceStats: APIFunctionType<GetSpaceStatsParams, SpaceStatsData>;
  /**
   * @description 统计团队空间存储流量详情列表
   */
  getTeamUsedInfo: (params: ListTableParams) => RequestResponse<SpaceUsedInfoData<TeamSpaceUsedInfoData>>;
  /**
   * @description 统计个人空间存储流量详情列表
   */
  getPersonalUsedInfo: (params: ListTableParams) => RequestResponse<SpaceUsedInfoData<PersonalSpaceUsedInfoData>>;
  /**
   * @description 统计流量统计概览
   */
  trafficUsageOverview: () => RequestResponse<TrafficUsageOverviewData>;
  /**
   * @description 统计时序流量统计
   */
  trafficUsageTiming: (params: TrafficUsageTimingParams) => RequestResponse<TrafficUsageTimingData>;

  /**
   * @description 查询消息列表
   */
  listMessage: APIFunctionType<ListMessageParams, ListMessageData>;
  /**
   * @description 标记所有消息已读
   */
  readAllMessage: APIFunctionType<{ type: number }, ListMessageData>;
  /**
   * @description 删除所有消息
   */
  deleteAllMessage: APIFunctionType<{ type: number }, ListMessageData>;
  /**
   * @description 删除某一条消息
   */
  deleteMessageItem: APIFunctionType<{ messageId: number }, ListMessageData>;
  /**
   * @description 查询用户接收消息配置
   */
  getSettingConfig: APIFunctionType<null, SettingConfigData>;
  /**
   * @description 更新用户接收消息配置
   */
  updateSettingConfig: APIFunctionType<SettingConfigData, EmptyData>;
  /**
   * @description 批量删除消息
   */
  deleteMessageList: APIFunctionType<MessageInfo, EmptyData>;
  /**
   * @description 批量标记消息已读
   */
  readMessageList: APIFunctionType<MessageInfo, EmptyData>;

  /**
   * @description 设置用户空间配额
   */
  setCapacity: APIFunctionType<{ userId: number; capacity: string | null }, EmptyData>;
  /**
   * @description 获取文件或目录的详细信息
   */
  getDirectoryOrFileInfo: APIFunctionType<getDirectoryOrFileInfoParams, getDirectoryOrFileInfoData>;
  /**
   * @description 申请生成订单借口而
   */
  midasPurchase: APIFunctionType<{ isNew: boolean; productid: string; extensionData: object }, { purchaseId: string }>;
  /**
   * @description 查询订单信息
   *
   */
  midasGetPurchaseId: APIFunctionType<{ purchaseId: string }, MidasGetPurchaseIdData>;
  /**
   * @description TODO: Boss 回调接口
   */
  // midasBoss: APIFunctionType<{ isNew: boolean }, { purchaseId: string }>
  /**
   * @description 获取角色列表
   */
  getRoleList: APIFunctionType<null, Role[]>;
  /**
   * @description 创建角色列表
   */
  createRoleList: APIFunctionType<null, CustomRole[], null, CustomRole[]>;
  /**
   * @description 更新角色列表
   */
  updateRoleList: APIFunctionType<null, EmptyData, null, CustomRole[]>;
  /**
   * @description 删除自定义角色
   */
  deleteRoleList: APIFunctionType<{ roleIds: Number[]; replaceRoleId: Number }, EmptyData>;
  /**
   * @description 查询用户
   */
  getUser: APIFunctionType<{ userId?: number; withBelongingTeams?: boolean; pf?: ChannelTag }, UserBaseInfo>;
  /**
   * @description 查询用户列表
   */
  getUserList: APIFunctionType<GetUserListParams, GetUserListData>;

  /**
   * @description 查询外部用户列表
   */
  getExtUserList: APIFunctionType<GetExtUserListParams, GetExtUserListData>;
  /**
   * @description 创建用户
   */
  createUser: APIFunctionType<CreateUserParams, CreateUserData>;
  /**
   * @description 创建协作用户
   */
  createExtUser: APIFunctionType<CreateExtUserParams, CreateExtUserData>;
  /**
   * @description 修改用户
   */
  updateUser: APIFunctionType<UpdateUserParams, CreateUserData>;
  /**
   * @description 修改协作用户
   */
  updateExtUser: APIFunctionType<UpdateExtUserParams, CreateUserData>;
  /**
   * @description 删除用户
   */
  deleteUsers: APIFunctionType<DeleteUsersParams, EmptyData>;
  /**
   * @description 删除用户
   */
  deleteExtUsers: APIFunctionType<DeleteUsersParams, EmptyData>;
  /**
   * @description 启用禁用用户
   */
  toggleUsersAvailable: APIFunctionType<{ userIds: string; enabled: boolean }, EmptyData>;
  /**
   * @description 启用禁用用户
   */
  toggleExtUsersAvailable: APIFunctionType<{ userIds: string; enabled: boolean }, EmptyData>;
  /**
   * @description 设置用户权限
   */
  updateUsersRole: APIFunctionType<{ userIds: string; role: string }, EmptyData>;
  /**
   * @description 上传用户头像(获取上传路径)
   */
  putAvatar: APIFunctionType<{ ext: string }, PutAvatarData>;
  /**
   * @description 确认上传用户头像
   */
  postAvatar: APIFunctionType<{ filePath: string }, PostAvatarData>;
  /**
   * @description 确认上传用户头像
   */
  updatePhoneSend: APIFunctionType<{ countryCode: string; phoneNumber: string }, EmptyData>;
  /**
   * @description 确认上传用户头像
   */
  updatePhoneConfirm: APIFunctionType<{ countryCode: string; phoneNumber: string; code: string }, EmptyData>;
  /**
   * @description 给用户设置配额
   */
  setUserQuota: APIFunctionType<{ userId: number; capacity: string }, EmptyData>;
  /**
   * @description 上传excel(获取上传路径)
   */
  getXlsxInfo: APIFunctionType<{ ext: string }, PutAvatarData>;
  /**
   * @description 检查用户 xlsx 文件
   */
  checkXlsx: APIFunctionType<{ filePath: string }, Task<AsyncTaskResult[]>>;
  /**
   * @description 导入用户 xlsx 文件
   */
  uploadXlsx: APIFunctionType<{ filePath: string }, Task<AsyncTaskResult[]>>;
  /**
   * @description 查询用户所属群组
   */
  getUserOwnerGroup: APIFunctionType<{ userIds: string }, GetUserOwnerGroupData[]>;
  /**
   * @description 手动激活用户
   */
  activeUser: APIFunctionType<{ userId: number }, EmptyData>;
  /**
   * @description 查询空间大小
   */
  getUserCapacity: APIFunctionType<{ userIds: string }, UserCapacityData>;

  /**
   * @description 创建团队
   */
  createTeam: APIFunctionType<CreateTeamParams, { id: number }>;
  /**
   * @description 修改团队
   */
  updateTeam: APIFunctionType<UpdateTeamParams, EmptyData>;
  /**
   * @description 删除团队
   */
  deleteTeam: APIFunctionType<DeleteTeamParams, EmptyData>;
  /**
   * @description 查询团队列表
   */
  queryTeam: APIFunctionType<QueryTeamParams, QueryTeamData>;
  /**
   * @description 添加团队成员
   */
  addTeamMember: APIFunctionType<{ teamId: number; userIds: number[]; authorizeTo?: DirAuthorize[] }, EmptyData>;
  /**
   * @description 查询团队成员
   */
  queryTeamMember: APIFunctionType<QueryTeamMemberParams, QueryTeamMemberData>;
  /**
   * @description 删除团队成员
   */
  deleteTeamMember: APIFunctionType<{ teamId: number; userIds: number[] }, EmptyData>;
  /**
   * @description 修改成员权限
   */
  editTeamMemberFileRole: APIFunctionType<{ teamId: number; authorizeTo?: DirAuthorize[] }, EmptyData>;
  /**
   * @description 添加团队管理员
   */
  addTeamAdmin: APIFunctionType<{ teamId: number; userIds: string }, EmptyData>;
  /**
   * @description 删除团队管理员
   */
  deleteTeamAdmin: APIFunctionType<{ teamId: number; userIds: string }, EmptyData>;

  /**
   * @description 创建群组
   */
  createCollaborationSpace: APIFunctionType<CreateColleborationSpaceParams, { id: number }>;
  /**
   * @description 查询群组
   */
  getCollaborationSpaceInfo: APIFunctionType<{ groupId: number; spaceOrgId: number }, GetColleborationSpaceInfoData>;
  /**
   * @description 列出用户所在群组
   */
  listCollaborationSpaces: APIFunctionType<ListColleborationSpacesParams, ListColleborationSpaceInfoData>;
  /**
   * @description 更新群组
   */
  updateCollaborationSpace: APIFunctionType<{ groupId: number; name: string }, EmptyData>;
  /**
   * @description 删除群组
   */
  deleteCollaborationSpace: APIFunctionType<{ groupId: number }, EmptyData>;
  /**
   * @description 添加群组成员
   */
  addCollaborationSpaceMembers: APIFunctionType<{ groupId: number; users: ColleborationMember[] }, EmptyData>;
  /**
   * @description 修改群组成员角色/权限
   */
  updateCollaborationSpaceMembers: APIFunctionType<{ groupId: number; users: ColleborationMember[] }, EmptyData>;
  /**
   * @description 查询群组成员
   */
  getCollaborationSpaceMembers: APIFunctionType<
    GetCollaborationSpaceMembersParams,
    ListTableData<ColleborationMemberDetail>
  >;
  /**
   * @description 删除群组成员
   */
  deleteCollaborationSpaceMember: APIFunctionType<
    {
      groupId: number;
      users: Array<{ userId: number; orgId: number }>;
    },
    EmptyData
  >;
  /**
   * @description 退出群组（非群主）
   */
  exitCollaborationSpace: APIFunctionType<{ groupId: number }, EmptyData>;
  /**
   * @description 列出群组空间
   */
  getGroupSpace: APIFunctionType<{ groupId: number; spaceOrgId: number }, TeamSpace>;
  /**
   * @description 查看收藏列表
   */
  getFavoriteList: APIFunctionType<GetFavoriteListParams, GetFavoriteListData>;
  /**
   * @description 添加收藏
   */
  addFavorite: APIFunctionType<AddFavoriteParams, AddFavoriteData>;
  /**
   * @description 取消收藏
   */
  delFavorite: APIFunctionType<{ favoriteIds: string; spaceOrgId?: number }, EmptyData>;
  /**
   * @description 原：查看文件目录收藏状态接口，现：改为获取文件额外信息接口
   * 收藏状态隶属于扩展信息的一个
   */
  getFileExtraInfo: APIFunctionType<{}, GetFileExtraInfoData[]>;
  /**
   * 获取图片缩略图（万象获取，较为耗时，所以需要与 extras 分开）
   */
  getImageThumbnail: APIFunctionType<{}, GetImageThumbnailResult>;
  /**
   * @description 创建搜索日志任务
   */
  createSearchTeamTask: APIFunctionType<CreateSearchTeamTaskParams, SearchTaskData<TeamTreeNode>>;
  /**
   * @description 继续列出搜索日志
   */
  listSearchTeamContent: APIFunctionType<ListSearchContentParams, SearchTaskData<TeamTreeNode>>;
  /**
   * @description 删除搜索任务
   */
  deleteSearchTeamTask: APIFunctionType<DeleteSearchTaskParams, EmptyData>;
  /**
   * @description 创建下载日志任务
   */
  createDownloadLogTask: APIFunctionType<CreateDownloadLogTaskParams, { taskId: number }>;
  /**
   * @description 查询下载日志任务
   */
  listDownloadLogTask: APIFunctionType<null, ListDownloadLogTaskData>;
  /**
   * @description 创建搜索日志任务
   */
  createSearchLogTask: APIFunctionType<CreateSearchLogTaskParams, SearchTaskData<SearchLogItem>>;
  /**
   * @description 继续列出搜索日志
   */
  listSearchLog: APIFunctionType<ListSearchLogParams, SearchTaskData<SearchLogItem> & SearchLogResult>;
  /**
   * @description 删除搜索任务
   */
  delSearchInLog: APIFunctionType<DeleteSearchLogTaskParams, EmptyData>;
  /**
   * @description 列出接收的共享文件
   */
  getReceiveShareDirectory: APIFunctionType<ListShareParams, ListShareData>;
  /**
   * @description 列出与我相关文件
   */
  getRelatedToMeDirectory: APIFunctionType<ListTableParams, ListTableData<RelatedToMeFileItemData>>;
  /**
   * @description 获取最近使用文件列表
   */
  getRecentlyUsedDirectory: APIFunctionType<GetRecentlyUsedDirectoryParams, GetRecentlyUsedDirectoryData>;
  /**
   * @description 共享授权-查看文件权限
   */
  getDirPermissions: APIFunctionType<
    GetAuthorityDirectoryParams,
    { contents: GetAuthorityDirectoryData[]; nextMarker?: string }
  >;
  /**
   * @description 查看历史版本列表
   */
  getHistoryList: APIFunctionType<GetHistoryListParams, GetHistoryListData>;
  /**
   * @description 查询历史版本配置信息
   */
  getLibraryHistoryExtension: APIFunctionType<GetLibraryHistoryExtension, GetLibraryHistoryExtension>;
  /**
   * @description 设置系统历史版本配置
   */
  setVersionConfig: APIFunctionType<GetLibraryHistoryExtension, DeleteSyncData>;
  /**
   * @description 查询异步任务
   */
  queryTask: APIFunctionType<{ taskIdList: string }, TaskResult>;
  /**
   * @description 取消异步任务
   */
  cancelTask: APIFunctionType<{ taskId: number }, Task<AsyncTaskResult[]>>;
  /**
   * @description 检查腾讯会议授权是否有效
   */
  checkMeetingAuth: APIFunctionType<null, CheckMeetingAuthData>;
  /**
   * @description 腾讯会议授权
   */
  authMeeting: APIFunctionType<{ authCode: string }, any>;
  /**
   * @description  根据会议 code 获取加入链接
   */
  joinMeetingByCode: APIFunctionType<{ meetingCode: string }, joinMeetingData>;
  /**
   * @description  创建会议
   */
  createMeeting: APIFunctionType<createMeetingParams, joinMeetingData>;
  /**
   * @description  获取当前用户即将开始的会议
   */
  getMeetingLists: APIFunctionType<null, meetingListsData>;
  /**
   * @description  解绑腾讯会议
   */
  deleteMeetingAuth: APIFunctionType<null, any>;
  /**
   * @description  获取腾讯会议内鉴权签名
   */
  getMeetingSign: APIFunctionType<{ code: string; url: string }, any>;
  /**
   * @description  解绑微信
   */
  deleteWechatAuth: APIFunctionType<null, any>;
  /**
   * @description  解绑yufu
   */
  deleteYufuAuth: APIFunctionType<null, any>;
  /**
   * @description 删除任务
   */
  deleteTask: APIFunctionType<{ taskId: number }, EmptyData>;
  /**
   * @description 生成加入企业邀请码
   */
  generateInviteCode: APIFunctionType<GenerateInviteCodeParams, { code: string; domains: DomainItem[] }>;
  /**
   * @description 生成临时用户加入企业邀请码
   */
  generateExtInviteCode: APIFunctionType<null, { code: string; domains: DomainItem[] }>;
  /**
   * @description 查询邀请码信息
   */
  getInviteCodeInfo: APIFunctionType<{ code: string }, GetGroupInviteInfoData>;
  /**
   * @description 查询加入企业邀请码的信息
   * TODO 还未完善 GetInviteCodeData
   */
  getInviteCode: APIFunctionType<null, GetGroupInviteInfoData>;
  /**
   * @description 查询临时用户加入企业邀请码的信息
   */
  getExtInviteCode: APIFunctionType<null, GetGroupInviteInfoData>;
  /**
   * @description 生成加入群组邀请码
   */
  generateGroupInviteCode: APIFunctionType<GenerateGroupInviteCodeParams, { code: string }>;
  /**
   * @description 查询群组邀请码
   */
  getGroupInviteCode: APIFunctionType<{ groupId: number }, { code: string }>;
  /**
   * @description 查询群组邀请码详情
   */
  getGroupInviteInfo: APIFunctionType<{ code: string }, GetGroupInviteInfoData>;
  /**
   * @description 查询群组邀请码详情
   */
  updateInviteCode: APIFunctionType<UpdateInviteCodeParams, GetGroupInviteInfoData, null>;
  /**
   * @description 删除群组或企业邀请码code
   */
  deleteInviteCode: APIFunctionType<{ code: string }, EmptyData>;
  /**
   * @description  查询新手引导数据
   */
  queryNoviceGuidance: APIFunctionType<null, any>;
  /**
   * @description  生成新手指引数据
   */
  createNoviceGuidance: APIFunctionType<any, any>;
  /**
   * @description 获取外链邀请信息
   */
  getInvitationInfo: APIFunctionType<{ code: string }, GetInvitationInfoData>;
  /**
   * @description 获取外链邀请信息(需要登录用户信息)
   */
  getInvitationInfoWithUserInfo: APIFunctionType<{ code: string }, GetInvitationInfoData>;
  /**
   * @description 加入群组（邀请外链里使用）
   */
  joinGroup: APIFunctionType<{ code: string }, JoinGroupOrOrgData>;
  /**
   * @description 加入企业（邀请外链里使用）
   */
  joinOrg: APIFunctionType<{ code: string }, JoinGroupOrOrgData>;
  /**
   * @description 加入临时成员（邀请外链里使用）
   */
  joinTemporary: APIFunctionType<{ code: string }, JoinGroupOrOrgData>;
  /**
   * @description 检查是否账号可以注销
   */
  checkDeregister: APIFunctionType<null, CheckDeregisterData>;
  /**
   * @description 发起注销
   */
  deregister: APIFunctionType<null, EmptyData>;
  /**
   * @description 用户自己取消注销
   */
  cancelDeregister: APIFunctionType<{ organizationId: number }, EmptyData>;
  /**
   * @description 管理员驳回注销
   */
  rejectDeregister: APIFunctionType<{ userId: string }, EmptyData>;
  /**
   * @description 重新注册个人版
   */
  restorePersonalAccount: APIFunctionType<null, OrgItem>;
  /**
   * @description 查询电子签可用模板列表
   */
  getElectronicSignTemplates: APIFunctionType<null, GetElectronicSignTemplatesData>;
  /*
   * @description 获取电子签的key
   */
  getElectronSignKey: APIFunctionType<GetElectronSignKeyParams, { key: string }>;
  /**
   * 获取电子签名的状态
   */
  getElectronSignStatus: APIFunctionType<GetElectronSignStatusParams, GetElectronSignStatusData>;
  /**
   * 确认授权电子签
   */
  confirmElectronSignKey: APIFunctionType<ConfirmElectronSignKeyParams, EmptyData>;

  // 回收站操作接口
  /**
   * @description 列出回收站项目
   */
  listRecycled: APIFunctionType<ListRecycledParams, ListRecycledData>;
  /**
   * @description 永久删除指定回收站项目(可批量)
   */
  deleteRecycled: APIFunctionType<RecycledParams, PathStringList>;
  /**
   * @description 恢复指定回收站项目(可批量)
   */
  restoreRecycled: APIFunctionType<RecycledParams, PathStringList>;
  /**
   * @description 获取回收站目录详情
   */
  getRecycledItemInfo: APIFunctionType<GetRecycledItemInfoParams, RecycledItemBaseInfo>;
  /**
   * @description 查看ip限制列表
   */
  getIPRestrictionList: APIFunctionType<ListTableParams, ListTableData<IPRestrictionItem>>;
  /**
   * @description 查看管理员IP限制状态
   */
  getIPAdminStatus: APIFunctionType<null, { limitAdmin: boolean }>;
  /**
   * @description 新增ip限制后台配置
   */
  addIPRestriction: APIFunctionType<null, IPRuleItem[], null, IPRuleItem[]>;
  /**
   * @description 修改ip限制操作
   */
  editIPRestriction: APIFunctionType<EditIPRestrictionParams, EmptyData>;
  /**
   * @descripton 删除ip规则
   */
  deleteIPRestriction: APIFunctionType<{ id: number }, EmptyData>;
  /**
   * @descripton 修改管理员按钮限制操作，需要管理员权限
   */
  modifyIPAdminStatus: APIFunctionType<{ limitAdmin: boolean }, EmptyData>;
  /*
   * @description  外部用户获取上传下载的accessToken
   */
  getCollectAccessToken: APIFunctionType<
    {
      userToken: string;
      collectToken: string;
    },
    CollectAccessTokenData
  >;
  /**
   * @description  网盘用户历史记录
   */
  getCollectHistory: APIFunctionType<CollectTaskParams, CollectHistoryItem[]>;
  /**
   * @description  网盘用户获取上传下载的accessToken
   */
  getCollectAccessTokenInner: APIFunctionType<
    {
      collectToken: string;
    },
    CollectAccessTokenData
  >;
  /**
   * @description  获取收集进度
   */
  getCollectStatus: APIFunctionType<CollectTaskParams, CollectStatusData>;
  /**
   * @description 获取单个文件收集详情
   */
  getCollectTask: APIFunctionType<CollectTaskParams, CollectTaskData>;
  /**
   * @description 获取可疑文件的列表
   */
  getCloudHerbsList: APIFunctionType<CloudHerbsParams, CloudHerbsData>;
  /**
   * @description 给疑似病毒文件加白
   */
  trustVirusFile: APIFunctionType<{ restoreItems: TrustFileItem[] }, TrustFileData>;
  /**
   * @description 信任敏感文件
   */
  trustSensitiveFile: APIFunctionType<{ directoryList: TrustFileItem[] }, TrustFileData>;
  /**
   * @description 查询敏感词统计列表
   */
  getSensitiveCountList: APIFunctionType<Array<SensitiveDataParams>, any>;
  /**
   * @description 根据敏感词查询匹配到的文件列表
   */
  getSensitiveWordList: APIFunctionType<{ word: string }, SensitiveWordListData>;
  /**
   * @description 创建敏感词
   */
  createSensitiveWord: APIFunctionType<{ words: string[] }, CreateSensitiveData>;
  /**
   * @description 删除敏感词
   */
  deleteSensitiveWord: APIFunctionType<{ words: string[] }, CreateSensitiveData>;
  /**
   * @description 查询敏感词列表
   */
  getSensitiveList: APIFunctionType<Array<SensitiveDataParams>, any>;
  /**
   * @description 发起文件审批
   */
  addFileApply: APIFunctionType<FileApplyParams, FileApplyData>;
  /**
   * @description 同意文件审批
   */
  agreeFileApply: APIFunctionType<FileApplyNoParams, EmptyData>;
  /**
   * @description 审批列表(我申请的,我审批的,历史审批记录)
   */
  getFileApplyList: APIFunctionType<FileApplyPageParams, FileApplyPageData>;
  /**
   * @description 撤销文件审批
   */
  cancelFileApply: APIFunctionType<FileApplyNoParams, EmptyData>;
  /**
   * @description 查看文件审批详情
   */
  getFileApply: APIFunctionType<FileApplyNoParams, FileApplyDetailData>;
  /**
   * @description 检查文件是否在审批中
   */
  checkFileApply: APIFunctionType<FileApplyCheckParams, FileApplyCheckData[]>;
  /**
   * @description 驳回文件审批
   */
  rejectFileApply: APIFunctionType<FileApplyNoParams, EmptyData>;

  // 文件同步操作接口
  /**
   * @description 创建本地盘同步任务
   */
  createSync: APIFunctionType<CreatePersonalSyncParams | CreateTeamSyncParams, CreateSyncData>;
  /**
   * @description 查询本地盘同步任务
   */
  getSyncInfo: APIFunctionType<GetSyncInfoParams, GetSyncInfoData>;
  /**
   * @description 查询本地盘同步任务
   */
  querySyncInfo: APIFunctionType<QuerySyncInfoParams, QuerySyncInfoData[]>;
  /**
   * @description 删除本地盘同步任务
   */
  deleteSync: APIFunctionType<DeleteSyncParams, EmptyData>;
  /**
   * @description 新老同步盘升级接口
   */
  migrateSyncInfo: APIFunctionType<migrateSyncInfoParams, migrateSyncInfoData[]>;

  // 创建搜索任务接口
  /**
   * @description 创建搜索任务接口
   */
  createSearchTask: APIFunctionType<CreateSearchTaskParams, SearchTaskData<SearchFileData>>;
  /**
   * @description 自己修改密码
   */
  updatePassword: APIFunctionType<UpdatePasswordParams, EmptyData>;
  /**
   * @description 超管修改账号
   */
  updateAccountName: APIFunctionType<UpdateAccountNameParams, EmptyData>;
  /**
   * @description 生成重置密码token
   */
  createResetToken: APIFunctionType<CreateResetTokenParams, { resetToken: string }>;
  /**
   * @description 查询用户是否有重置token
   */
  getResetToken: APIFunctionType<GetResetTokenParams, GetResetTokenData>;
  /**
   * @description 查询token详情
   */
  getResetTokenDetail: APIFunctionType<GetResetTokenDetailParams, GetResetTokenDetailData>;
  /**
   * @description 使用token重置新密码
   */
  resetNewPassword: APIFunctionType<ResetNewPasswordParams, EmptyData>;
  /**
   * @description oauth2获取应用授权code
   */
  getOauthCode: APIFunctionType<GetOauthCodeParams, OauthData>;
  /**
   * @description oauth2获取应用授权说明信息
   */
  getOauthInfo: APIFunctionType<GetOauthInfoParams, OauthInfoData>;
}

/**
 * @description 路径非 user 开头的 API 接口集合
 */
export interface CommonRequestSet extends APISet {
  /**
   * @description 查询租户空间属性
   */
  getSpaceConfig: APIFunctionType<null, GetSpaceConfigData>;
  /**
   * @description 查询租户空间大小
   */
  getSpaceSize: APIFunctionType<null, GetSpaceSizeData>;
  /**
   * @description 创建租户空间
   */
  createSpace: APIFunctionType<null, CreateSpaceData>;
  /**
   * @description 修改租户空间属性
   */
  updateSpace: APIFunctionType<null, EmptyData>;
  /**
   * @description 删除租户空间
   */
  deleteSpace: APIFunctionType<null, EmptyData>;

  /**
   * @description 访问令牌续期
   */
  renewAccessToken: APIFunctionType<{ accessToken: AccessToken }, RenewAccessTokenData>;
  /**
   * @description 删除指定的访问令牌
   */
  deleteAccessToken: APIFunctionType<null, EmptyData>;
  /**
   * @description 保存至我的网盘
   */
  saveToCloudDisk: APIFunctionType<SaveAsParams, any, null, BatchCopyItem[]>;
  /**
   * @description 列出目录或相簿内容
   */
  listDirectory: APIFunctionType<ListDirectoryParams, ListDirectoryData>;
  /**
   * @description 创建目录或相簿
   */
  createDirectory: APIFunctionType<CreateDirectoryParams, PathStringList>;
  /**
   * @description 删除目录
   */
  deleteDirectory: APIFunctionType<DeleteDirectoryParams, DeleteDirectoryData>;
  /**
   * @description 重命名/移动目录或相簿
   */
  renameOrMoveDirectory: APIFunctionType<RenameOrMoveDirectoryParams, RenameOrMoveDirectoryData>;
  /**
   * @description 复制目录或相簿
   */
  copyDirectory: APIFunctionType<CopyDirectoryParams, PathStringList>;
  /**
   * @description 跨空间复制目录或相簿
   */
  crossSpaceCopyDirectory: APIFunctionType<CrossSpaceCopyDirectoryParams, PathStringList>;
  /*
   * @description 检查目录或相簿状态
   */
  inspectDirectory: APIFunctionType<DirectoryParams, EmptyData>;
  /**
   * @description 查看目录或相簿详情
   */
  getDirectoryInfo: APIFunctionType<DirectoryParams & { purpose?: 'download' | 'preview' }, SpaceFileItemData>;
  /**
   * @description 获取下载文件信息(不包含cosUrl，不计下载次数)
   */
  getDownloadFileInfo: APIFunctionType<
    FileParams & { purpose?: 'download' | 'preview'; preview?: boolean; pre_check?: boolean },
    GetFileInfoData
  >;
  /**
   * @description 获取下载文件cosUrl(调用一次，记一次下载)
   */
  getDownloadCosUrl: APIFunctionType<FileParams & cosUrlInfoParams, GetCosUrlData>;
  /**
   * @description 验预览和下载是否达到上限
   * @param check_preview_and_download_times: 校验预览和下载是否达到上限：1是预览 2下载
   */
  checkPreviewAndDownloadTime: APIFunctionType<FileParams & { check_preview_and_download_times: 1 | 2 }, any>;
  /**
   * @description 获取编辑文件信息（主要用于判断编辑是否有报错时使用）
   */
  checkEditFile: APIFunctionType<FileParams, any>;
  /*
   * @description 检查文件状态
   */
  inspectFile: APIFunctionType<InspectFileParams, EmptyData>;
  /**
   * @description 检查文件上传的空间大小
   */
  checkFileStorage: APIFunctionType<CheckFileParams, UploadPartInfo>;
  /**
   * @description 开始简单上传文件
   */
  uploadFile: APIFunctionType<UploaFileParams, UploadFileData>;
  /**
   * @description 开始分块上传文件
   */
  uploadMultiPartFile: APIFunctionType<UploaFileParams, UploadMultiPartFileData>;
  /**
   * @description 分块上传任务续期
   */
  updateMultiPartFileInfo: APIFunctionType<UpdateUploadMultiPartInfo, UpdateMultiPartFileInfoData>;
  /**
   * @description 完成上传文件
   */
  confirmUploadFile: APIFunctionType<ConfirmUploadFileParams, ConfirmUploadFileData>;
  /**
   * @description 获取文件上传任务状态
   */
  getFileUploadStatus: APIFunctionType<ConfirmUploadFileParams, GetFileUploadStatusData>;
  /**
   * @description 取消上传任务
   */
  cancelFileUpload: APIFunctionType<CancelFileUploadParams, EmptyData>;
  /**
   * @description 用模板创建文档
   */
  createDoc: APIFunctionType<FileCreateDocParams, SpaceFileItemData>;
  /**
   * @description 从模板库导入文件
   */
  createStencilFile: APIFunctionType<FileCreateStencilParams, any>;
  /**
   * @description 删除文件
   */
  deleteFile: APIFunctionType<DeleteFileParams, DeleteFileData>;
  /**
   * @description 重命名/移动文件
   */
  renameOrMoveFile: APIFunctionType<RenameOrMoveFileParams, PathStringList | null>;
  /**
   * @description 复制文件
   */
  copyFile: APIFunctionType<CopyFileParams, PathStringList>;
  /**
   * @description 批量重命名/移动
   */
  batchMoveOrRename: APIFunctionType<{ spaceId: string }, any, null, MoveRequestItem[]>;
  /**
   * @description 批量删除
   */
  batchDelete: APIFunctionType<{ spaceId: string }, any, null, BatchDeleteItem[]>;
  /**
   * @description 批量复制
   */
  batchCopy: APIFunctionType<{ spaceId: string }, any, null, CopyRequest[]>;
  /**
   * @description 批量下载
   */

  batchDownload: APIFunctionType<{ spaceId: string; shareToken?: string }, BatchDownloadData>;
  // 文件同步操作接口
  /**
   * @description 创建本地盘同步任务
   */
  createSync: APIFunctionType<CreateSyncParams, CreateSyncData>;
  /**
   * @description 查询本地盘同步任务
   */
  getSyncInfo: APIFunctionType<GetSyncInfoParams, GetSyncInfoData>;
  /**
   * @description 删除本地盘同步任务
   */
  deleteSync: APIFunctionType<DeleteSyncParams, DeleteSyncData>;

  // 历史版本接口
  /**
   * @description 删除历史版本
   */
  deleteHistory: APIFunctionType<{ spaceId: string | undefined }, EmptyData, null, number[]>;
  /**
   * @description 设置历史版本为最新版本
   */
  setLatestVersion: APIFunctionType<SetLatestVersionParams, DeleteSyncData>;

  // 安全扫描接口
  /**
   * @description 获取安全扫描开关信息
   */
  getSecurityScanConfig: APIFunctionType<null, GetSecurityScanParams>;
  /**
   * @description 设置安全扫描开关信息
   */
  setSecurityScanConfig: APIFunctionType<GetSecurityScanParams, null>;
  /**
   * @description 防勒索通知
   */
  ransomwareWarn: APIFunctionType<RansomwareWarnParams, null>;

  // 文件共享接口
  /**
   * @description 列出发送的共享文件
   */
  getSendShareDirectory: APIFunctionType<ListShareParams, ListShareData>;
  // 共享授权接口
  /**
   * @description 为文件夹分配权限
   */
  postDirPermission: APIFunctionType<PutAuthorityDirectoryParams, ListShareData>;
  /**
   * @description 删除文件夹的权限
   */
  deleteDirPermissions: APIFunctionType<DeleteAuthorityDirectoryParams, ListShareData>;

  // 异步任务接口
  /**
   * @description 批量操作异步任务查询
   */
  asyncTaskQuery: APIFunctionType<AsyncTaskParams, any>;

  // 标签接口
  /**
   * @description 创建标签
   */
  createTags: APIFunctionType<CreateFileTagParams, EmptyData>;
  /**
   * @description 给文件设置标签
   */
  setFileTags: APIFunctionType<{ filePath: string; spaceId: string }, EmptyData, null, SetFileTagsBody>;

  /**
   * @description 根据电子签模板生成文件（同【用模板创建文件接口】）
   */
  getFileByTemplateId: APIFunctionType<{ filePath: string; spaceId: string; fromTemplate: string }, SpaceFileItemData>;
  /**
   * @description 游客历史记录
   */
  getVisitorCollectHistory: APIFunctionType<CollectTaskParams, CollectHistoryItem[]>;
  /**
   * @description 文件收集规则保存
   */
  saveCollectRule: APIFunctionType<{ collectToken: string; rules: { [key: string]: string } }, EmptyData>;
  /**
   * @description 文件收集规则获取
   */
  getCollectRule: APIFunctionType<{ collectToken: string }, { rules: { [key: string]: string } }>;
  /**
   * 获取文件模板类型
   */
  getStencilType: APIFunctionType<{ spaceId: string; spaceOrgId: number }, StencilTypeData[]>;
  /**
   * 获取文件模板列表
   */
  getStencilList: APIFunctionType<StencilListParams, StencilListData>;
}

/**
 * @description 同步盘独有的 API 接口集合
 * @export
 * @interface SyncRequestSet
 * @extends {APISet}
 */
export interface SyncRequestSet extends APISet {
  /**
   * @description 拉取 journal 日志
   */
  getJournal: APIFunctionType<GetJournalParams, GetJournalData>;
  /**
   * @description 查询文件/子目录是否存在
   */
  checkPathAvailabel: APIFunctionType<CheckPathAvailabelParams, EmptyData>;
  /**
   * @description 获取文件/子目录信息和文件下载地址
   */
  getPathInfo: APIFunctionType<GetPathInfoParams, SyncPathInfoData>;
  /**
   * @description 获取文件下载的cosUrl
   */
  getPathCosUrl: APIFunctionType<CheckPathAvailabelParams, SyncPathCosUrlData>;
  /**
   * @description 列出子目录
   */
  listPath: APIFunctionType<ListPathParams, ListPathData>;
  /**
   * @description 开始简单上传文件
   */
  uploadFile: APIFunctionType<SyncUploadFileParams, SyncUploadFileData>;
  /**
   * @description 开始分块上传文件
   */
  uploadMultiPartFile: APIFunctionType<SyncUploadFileParams, SyncUploadMultiPartFileData>;
  /**
   * @description 分块上传任务续期
   */
  updateMultiPartFileInfo: APIFunctionType<SyncUpdateUploadMultiPartInfo, SyncUpdateMultiPartFileInfoData>;
  /**
   * @description 完成上传文件
   */
  confirmUploadFile: APIFunctionType<SyncConfirmUploadFileParams, SyncConfirmUploadFileData>;
  /**
   * @description 获取文件上传任务状态
   */
  getFileUploadStatus: APIFunctionType<SyncConfirmUploadFileParams, SyncGetFileUploadStatusData>;
  /**
   * @description 取消上传任务
   */
  cancelFileUpload: APIFunctionType<SyncCancelFileUploadParams, EmptyData>;
  /**
   * @description 批量操作
   */
  batchOperate: APIFunctionType<SyncBatchOperateParams, SyncBatchOperateData, null, SyncBatchOperateBodyParams>;
}
